import { isTicketOverdue } from "../utils/ticketDueDate";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { ticketsApi, techniciansApi, commentsApi, emailApi, historyApi, emailThreadApi, attachmentsApi, aiApi, sharepointApi, companiesApi, contactsApi, projectsApi, maintenanceApi, agentRunsApi, agentsApi, knowledgeApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { useNotification } from "../context/NotificationContext";
import { attachmentCount, ticketHasAttachments } from "../utils/attachments";
import { confirmTicketDeletion } from "../utils/ticketDeletion";
import { detailText } from "../utils/apiErrors";
import { agentsForTicket, assignedStartAgentId } from "../utils/agentScope";
import RichTextEditor, { isRichTextEmpty, escapeHtml, plainTextToEditorHtml } from "../components/RichTextEditor";
import { AttachmentThumbnail, SecureEmailHtml, SecureInlineHtml } from "../components/SecureEmailHtml";
import AgentRunPanel from "../components/AgentRunPanel";
import ConversationPanel from "../components/ConversationPanel";
import EmployeeAvatar from "../components/EmployeeAvatar";
import { degradedAnalysisLabel } from "../components/degradedAnalysisVocabulary";
import TicketEmployeeWorkbench from "../components/TicketEmployeeWorkbench";
import { actionTypeLabel } from "../components/actionTypeVocabulary";
import { ticketCategoryLabel } from "../components/ticketCategoryVocabulary";
import { conversationStatusMeta } from "../components/conversationStatusVocabulary";
import { TICKET_STATUS_EDIT_ORDER, ticketStatusLabel } from "../components/ticketStatusVocabulary";

const asUtc = (iso) => /Z|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z";
const timeValue = (...values) => {
  const value = values.find(Boolean);
  return value ? new Date(asUtc(value)).getTime() : 0;
};

// The wording and the "which arms may a technician set by hand" rule live in ticketStatusVocabulary
// (see its header: unassigned is real and reachable and reads "לא משויכת", not maintenance/project's
// "לא הוקצה", and neither unassigned nor merged is a manual target). Only the badge colours are this
// page's own.
const STATUS_COLORS = {
  unassigned:  "bg-gray-100 text-gray-600 border-gray-300",
  open:        "bg-blue-100 text-blue-700 border-blue-300",
  in_progress: "bg-yellow-100 text-yellow-700 border-yellow-300",
  pending:     "bg-purple-100 text-purple-700 border-purple-300",
  waiting:     "bg-cyan-100 text-cyan-700 border-cyan-300",
  closed:      "bg-gray-100 text-gray-500 border-gray-300",
  merged:      "bg-teal-100 text-teal-700 border-teal-300",
};
const PRIORITY_LABELS = { low: "נמוכה", medium: "בינונית", high: "גבוהה" };

const HISTORY_ICONS = {
  created:                  "📋",
  created_from_email:       "📨",
  status_changed:           "🔄",
  ticket_closed:            "✅",
  ticket_reopened:          "↩️",
  priority_changed:         "⚡",
  title_changed:            "✏️",
  description_changed:      "📝",
  technician_assigned:      "👤",
  company_assigned:         "🏢",
  sender_name_changed:      "👤",
  sender_email_changed:     "📧",
  contact_selected:         "📇",
  contact_created:          "➕",
  internal_comment_added:   "💬",
  customer_comment_added:   "💬",
  internal_note_sent:       "📨",
  comment_edited:           "✏️",
  email_sent_customer:      "📧",
  email_sent_technician:    "📩",
  email_reply_received:     "↩️",
  attachment_added:         "📎",
  merged:                   "🔗",
  split:                    "🔀",
  queue_changed:            "📤",
  ai_summary_updated:       "🤖",
  ai_classification_updated: "🤖",
  ai_run_started:           "🤖",
  ai_analysis_complete:     "🤖",
  ai_analysis_approved:     "✅",
  ai_analysis_rejected:     "❌",
  ai_run_paused:            "⏸️",
  ai_run_resumed:           "▶️",
  ai_run_failed:            "⚠️",
  ai_action_add_comment:    "🤖",
  ai_action_close_ticket:   "🤖",
  ai_action_escalate:       "🤖",
  ai_action_send_customer_message: "🤖",
};

const HISTORY_LABELS = {
  created:                  "קריאה נפתחה",
  created_from_email:       "קריאה נפתחה ממייל",
  status_changed:           "שינוי סטטוס",
  ticket_closed:            "קריאה נסגרה",
  ticket_reopened:          "קריאה נפתחה מחדש",
  priority_changed:         "שינוי עדיפות",
  title_changed:            "שינוי כותרת",
  description_changed:      "שינוי תיאור",
  technician_assigned:      "שינוי טכנאי",
  company_assigned:         "שינוי חברה",
  sender_name_changed:      "עדכון שולח",
  sender_email_changed:     "עדכון שולח",
  contact_selected:         "נבחר איש קשר",
  contact_created:          "איש קשר נוסף",
  internal_comment_added:   "הערה פנימית",
  customer_comment_added:   "תגובה ללקוח",
  internal_note_sent:       "נשלחה הערה פנימית",
  comment_edited:           "הערה נערכה",
  email_sent_customer:      "מייל נשלח",
  email_sent_technician:    "מייל נשלח",
  email_reply_received:     "תגובה התקבלה",
  attachment_added:         "קובץ נוסף",
  merged:                   "איחוד קריאות",
  split:                    "פיצול קריאה",
  queue_changed:            "שינוי תור",
  ai_summary_updated:       "AI — סיכום עודכן",
  ai_classification_updated: "AI — סיווג עודכן",
  ai_run_started:           "AI — הרצה התחילה",
  ai_analysis_complete:     "AI — ניתוח הושלם",
  ai_analysis_approved:     "AI — ניתוח אושר",
  ai_analysis_rejected:     "AI — ניתוח נדחה",
  ai_run_paused:            "AI — הרצה הושהתה",
  ai_run_resumed:           "AI — הרצה חודשה",
  ai_run_failed:            "AI — הרצה נכשלה",
  ai_action_add_comment:    "AI — הוסיף הערה",
  ai_action_close_ticket:   "AI — סגר קריאה",
  ai_action_escalate:       "AI — הסלים לעדיפות גבוהה",
  ai_action_send_customer_message: "AI — שלח מסר ללקוח",
};

const AI_BLOCKING_STATUSES = new Set([
  "pending", "analyzing", "diagnosing", "executing",
  "awaiting_technician_review", "awaiting_approval",
  "waiting_for_customer_connection", "waiting_for_customer_answer",
  "connection_active", "paused",
]);

// ── Digital Employee activity (compact ticket-screen panel) ───────────────────
// Human-language wording for the run statuses the employee-activity endpoint returns, so the
// ticket screen never shows the raw enum. Its own wording rather than
// `components/runStatusVocabulary`'s: this is a compact panel, so it can afford the longer
// "ממתין לסקירת טכנאי" / "מבצע את הפעולה" where a chip cannot, and it falls back to "בעבודה" for a
// status it does not hold rather than naming it. `runStatusVocabulary.test.js` asserts this map
// keeps all 15 arms, so deleting one is a red test and not a status that silently reads as "בעבודה".

export const EMPLOYEE_ACTIVITY_STATUS_LABELS = {
  pending:                         "מאתחל...",
  analyzing:                       "מנתח קריאה...",
  diagnosing:                      "מאבחן...",
  connection_active:               "חיבור פעיל — מאבחן",
  executing:                       "מבצע את הפעולה",
  awaiting_technician_review:      "ממתין לסקירת טכנאי",
  awaiting_approval:               "ממתין לאישור",
  waiting_for_customer_connection: "ממתין לחיבור ללקוח",
  waiting_for_customer_answer:     "ממתין לתשובת לקוח",
  paused:                          "מושהה",
  completed:                       "הושלם",
  resolved:                        "נפתרה",
  failed:                          "נכשל",
  rejected:                        "נדחה",
  approved:                        "אושר",
};

export const EMPLOYEE_ACTIVITY_WAIT_LABELS = {
  technician_review: "ממתין לסקירת טכנאי",
  approval:          "ממתין לאישור",
};

// Mirrors NotificationBell's timeAgo, including the naive-string-is-UTC rule: an absolute date
// past a month reads better than "לפני 43 ימים".
export function employeeActivityTimeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(asUtc(iso)).getTime();
  if (Number.isNaN(diff)) return "";
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "עכשיו";
  if (m < 60) return `לפני ${m} דקות`;
  const h = Math.floor(m / 60);
  if (h < 24) return `לפני ${h} שעות`;
  const d = Math.floor(h / 24);
  if (d < 30) return `לפני ${d} ימים`;
  return new Date(asUtc(iso)).toLocaleDateString("he-IL");
}

export function employeeActivityStatusText(status) {
  return EMPLOYEE_ACTIVITY_STATUS_LABELS[status] || "בעבודה";
}

export function fetchTicketEmployeeActivity(ticketId) {
  return api.get(`/api/tickets/${ticketId}/employee-activity`).then((r) => r.data);
}

function fmtFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function compactText(text, max = 120) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return "";
  if (clean.length <= max) return clean;
  return clean.slice(0, max - 3).trimEnd() + "...";
}

// Recipient display: "Name <email>" when the address matches a known
// contact (resolved server-side, see tickets.py _attach_recipient_info),
// otherwise just the raw email address.
function formatRecipient(r) {
  return r.display_name ? `${r.display_name} <${r.email}>` : r.email;
}

function commentEmailUpdateInfo(comment) {
  const snapshot = comment.email_recipients_snapshot;
  if (!snapshot) {
    return { tone: "unknown", text: "פרטי נמעני המייל אינם זמינים" };
  }

  const recipients = Array.isArray(snapshot.recipients)
    ? snapshot.recipients.filter((recipient) => recipient?.email?.trim())
    : [];

  if (snapshot.status === "sent" && recipients.length > 0) {
    return {
      tone: "sent",
      text: `עדכון במייל נשלח אל: ${recipients.map(formatRecipient).join(", ")}`,
    };
  }

  return { tone: "none", text: "לא נשלח עדכון במייל" };
}

function CommentEmailUpdateLine({ info }) {
  const toneClass =
    info.tone === "sent"
      ? "border-blue-100 bg-blue-50 text-blue-700"
      : info.tone === "none"
      ? "border-gray-200 bg-white text-gray-500"
      : "border-amber-100 bg-amber-50 text-amber-700";

  return (
    <div dir="rtl" className={`mb-2 rounded-md border px-3 py-1.5 text-xs leading-5 ${toneClass}`}>
      {info.text}
    </div>
  );
}

export function TicketSummaryPanel({ summary }) {
  return (
    <section className="rounded-xl border border-violet-200 bg-violet-50/60 p-3 xl:p-4" aria-labelledby="ticket-summary-heading">
      <h2 id="ticket-summary-heading" className="text-sm font-semibold text-violet-800">
        סיכום הקריאה
      </h2>
      {summary ? (
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{summary}</p>
      ) : (
        <p className="mt-2 text-sm italic text-gray-400">עדיין לא קיים סיכום לקריאה זו</p>
      )}
    </section>
  );
}

export function TicketDescriptionPanel({
  ticket,
  attachments = [],
  isEditing = false,
  draftBody = "",
  onDraftBodyChange = () => {},
  onStartEdit = () => {},
  onSave = () => {},
  onCancel = () => {},
  isSaving = false,
}) {
  const html = ticket?.description_html;
  const plain = ticket?.description;
  // Emails store their original formatting in description_html, which this panel always
  // prefers over the plain field below — editing only the plain description would silently
  // leave the original email content on screen instead of the edit, so editing is limited
  // to tickets that don't carry stored email HTML.
  const canEdit = !html;

  return (
    <section className="rounded-xl border border-sky-200 bg-sky-50/60 p-3 xl:p-4" aria-labelledby="ticket-description-heading">
      <div className="flex items-center justify-between gap-2">
        <h2 id="ticket-description-heading" className="text-sm font-semibold text-sky-800">
          תקציר / תיאור הקריאה
        </h2>
        {canEdit && !isEditing && (
          <button
            type="button"
            onClick={onStartEdit}
            className="text-xs text-sky-700 hover:underline flex items-center gap-1 shrink-0"
          >
            ✏️ עריכה
          </button>
        )}
      </div>
      {canEdit && isEditing ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={draftBody}
            onChange={(e) => onDraftBodyChange(e.target.value)}
            rows={5}
            autoFocus
            className="w-full text-sm border border-sky-400 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 resize-y"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              className="px-3 py-1 text-xs bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {isSaving ? "שומר..." : "שמור"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="px-3 py-1 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              ביטול
            </button>
          </div>
        </div>
      ) : html ? (
        <div className="mt-2 max-w-full overflow-x-hidden rounded-lg bg-white">
          <SecureEmailHtml html={html} attachments={attachments} title="תיאור הקריאה" />
        </div>
      ) : plain ? (
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{plain}</p>
      ) : (
        <p className="mt-2 text-sm italic text-gray-400">לא הוזן תיאור לקריאה זו</p>
      )}
    </section>
  );
}

export function isSavedContactEmail(contacts, email) {
  const normalizedEmail = email?.trim().toLowerCase();
  return Boolean(normalizedEmail) && contacts.some(
    (contact) => contact.email?.trim().toLowerCase() === normalizedEmail
  );
}

export function contactDraftForEmailSender(sender, ticket) {
  const senderName = sender?.name ?? ticket?.sender_name ?? "";
  const senderEmail = sender?.email ?? ticket?.sender_email ?? "";
  const [firstName, ...rest] = senderName.trim().split(/\s+/);
  return {
    first_name: firstName || "",
    last_name: rest.join(" "),
    email: senderEmail,
    phone: "",
    company_id: ticket?.company_id || "",
  };
}

export function isDeletableInternalComment(comment, user) {
  return Boolean(
    comment?.is_internal &&
    comment.source !== "email_inbound" &&
    (comment.author_id === user?.id || ["owner", "admin", "technician"].includes(user?.role))
  );
}

export function confirmInternalCommentDeletion(commentId, confirm, onDelete) {
  if (!confirm("האם למחוק את ההערה? פעולה זו אינה ניתנת לביטול.")) return false;
  onDelete(commentId);
  return true;
}

export function contactPayloadFromSenderDraft(draft, sourceTicketId) {
  return {
    first_name: draft.first_name.trim(),
    last_name: draft.last_name.trim() || undefined,
    email: draft.email.trim(),
    phone: draft.phone.trim() || undefined,
    company_id: draft.company_id || undefined,
    source_ticket_id: sourceTicketId,
  };
}

export function SenderContactPhoneField({ value, onChange }) {
  return (
    <div>
      <label className="text-xs text-gray-400" htmlFor="sender-contact-phone">מספר טלפון (אופציונלי)</label>
      <input
        id="sender-contact-phone"
        type="tel"
        inputMode="tel"
        dir="ltr"
        value={value}
        onChange={onChange}
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
    </div>
  );
}

export function EmailSenderContactAction({ message, contacts, onAdd, isLoading = false }) {
  if (!message.from_email || isLoading) return null;

  if (isSavedContactEmail(contacts, message.from_email)) {
    return (
      <span className="shrink-0 text-xs font-medium text-emerald-600" title="השולח שמור באנשי הקשר">
        ✓ שמור
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onAdd({ email: message.from_email, name: message.from_name || "" })}
      className="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline"
    >
      + הוסף לאנשי קשר
    </button>
  );
}

function FileAttachmentPicker({ inputId, files, onAdd, onRemove }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          id={inputId}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const selected = Array.from(e.target.files || []);
            if (selected.length) onAdd(selected);
            e.target.value = "";
          }}
        />
        <label
          htmlFor={inputId}
          className="inline-flex items-center gap-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-colors"
        >
          <span>📎</span>
          צרף קובץ
        </label>
        {files.length > 0 && (
          <span className="text-xs text-gray-400">{files.length} קבצים נבחרו</span>
        )}
      </div>
      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((file, index) => (
            <div key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-2 rounded-lg border bg-white px-3 py-1.5 text-xs">
              <span className="text-gray-500">📎</span>
              <span className="font-medium text-gray-700 truncate">{file.name}</span>
              <span className="text-gray-400 whitespace-nowrap">({fmtFileSize(file.size)})</span>
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="mr-auto text-gray-400 hover:text-red-500"
              >
                הסר
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const hasHebrew = (text = "") => /[\u0590-\u05FF]/.test(text);

const SIGNATURE_FALLBACK_NAME = "צוות התמיכה Prodigy";
const SIGNATURE_FALLBACK_EMAIL = "support@prodigy.co.il";

// The signature is intentionally text-only (no logo/image) for every user —
// wrapped in a data-prodigy-signature marker div so the backend can reliably
// detect/strip it and rebuild it server-side before sending (see tickets.py
// _split_signature). Editing/removing it in the composer is always respected.
//
// Name resolution: `currentUser.display_name_he` is resolved by the backend
// (GET /api/users/me — see users.py) via the same technician-lookup helper
// used for Hebrew names everywhere else (audit log, comments) — this is the
// single source of truth. The local technicians-list match below is only a
// fallback for the rare case that field hasn't loaded yet; it must never be
// the primary source, or the two could silently disagree.
const buildEmailSignature = (currentUser, technicians = []) => {
  const currentEmail = currentUser?.email?.trim().toLowerCase();
  const matchedTechnician = currentEmail
    ? technicians.find((t) => t.email?.trim().toLowerCase() === currentEmail)
    : null;
  const technicianName = matchedTechnician
    ? `${matchedTechnician.first_name ?? ""} ${matchedTechnician.last_name ?? ""}`.trim()
    : "";

  const displayName =
    currentUser?.display_name_he?.trim() ||
    technicianName ||
    currentUser?.display_name?.trim() ||
    SIGNATURE_FALLBACK_NAME;
  const email = currentUser?.email?.trim() || matchedTechnician?.email?.trim() || SIGNATURE_FALLBACK_EMAIL;
  return (
    `<div data-prodigy-signature="true">בברכה,<br>` +
    `${escapeHtml(displayName)}<br>${escapeHtml(email)}<br>Prodigy</div>`
  );
};

export function appendEmailRecipient(currentValue, email) {
  const normalizedEmail = email?.trim();
  if (!normalizedEmail) return currentValue || "";

  const existing = (currentValue || "")
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);

  const alreadyExists = existing.some(
    (item) => item.toLowerCase() === normalizedEmail.toLowerCase()
  );

  if (alreadyExists) {
    return existing.join(", ");
  }

  return [...existing, normalizedEmail].join(", ");
}

export function emailRecipientDefaults(ticket) {
  return {
    to: ticket?.remembered_to_recipients || ticket?.sender_email || "",
    cc: ticket?.remembered_cc_recipients || "",
  };
}

export function EmailThreadCheckbox({ checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer w-fit">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
      />
      כלול שרשור מיילים
    </label>
  );
}

const contactSearchHaystack = (contact) =>
  [contact.first_name, contact.last_name, contact.email, contact.company_name, contact.phone]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const ccContactSearchHaystack = (contact) =>
  [contact.first_name, contact.last_name, contact.email]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export function AddContactModal({ title, search, onSearchChange, searchPlaceholder, onClose, children }) {
  // No Escape handler previously -- a keyboard user had to find the ✕ or the "סגור" button.
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" dir="rtl">
      <div role="dialog" aria-modal="true" aria-labelledby="add-contact-modal-title" className="bg-white rounded-xl shadow-xl border p-6 w-[28rem] max-w-[90vw] space-y-4">
        <div className="flex items-center justify-between">
          <h3 id="add-contact-modal-title" className="text-base font-semibold text-gray-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
            aria-label="סגור"
          >
            ✕
          </button>
        </div>

        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />

        <div className="space-y-1.5 max-h-72 overflow-y-auto">{children}</div>

        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
          >
            סגור
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function ContactCcPicker({ onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data: contacts = [], isLoading, isError } = useQuery({
    queryKey: ["contacts", "cc-picker", "all"],
    queryFn: () => contactsApi.list(),
  });

  const contactsWithEmail = contacts.filter((contact) => contact.email?.trim());
  const normalizedSearch = search.trim().toLowerCase();
  const filteredContacts = normalizedSearch
    ? contactsWithEmail.filter((contact) => ccContactSearchHaystack(contact).includes(normalizedSearch))
    : contactsWithEmail;

  const close = () => { setOpen(false); setSearch(""); };   // a kept search hid every other contact on the next open

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className="border rounded-lg px-3 py-2 text-sm text-gray-600 bg-white hover:bg-gray-50 transition-colors whitespace-nowrap"
      >
        + הוסף איש קשר
      </button>
      {open && (
        <AddContactModal
          title="בחירת איש קשר"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="חיפוש לפי שם או אימייל..."
          onClose={close}
        >
          {isLoading ? (
            <p className="py-5 text-center text-sm text-gray-400">טוען אנשי קשר...</p>
          ) : isError ? (
            <p className="py-5 text-center text-sm text-red-500">לא ניתן לטעון אנשי קשר</p>
          ) : contactsWithEmail.length === 0 ? (
            <p className="py-5 text-center text-sm text-gray-400">אין אנשי קשר במערכת</p>
          ) : filteredContacts.length === 0 ? (
            <p className="py-5 text-center text-sm text-gray-400">לא נמצאו אנשי קשר תואמים</p>
          ) : (
            filteredContacts.map((contact) => (
              <button
                key={contact.id}
                type="button"
                onClick={() => {
                  onSelect(contact.email);
                  close();
                }}
                className="w-full rounded-lg border px-3 py-2 text-right transition-colors hover:border-brand-400 hover:bg-brand-50"
              >
                <div className="truncate text-sm font-medium text-gray-800">
                  {contact.first_name} {contact.last_name || ""}
                </div>
                <div className="truncate text-xs text-gray-500" dir="ltr">
                  {contact.email}
                </div>
              </button>
            ))
          )}
        </AddContactModal>
      )}
    </div>
  );
}

// Pure lookup so Previous/Next always reflects exactly the order of whatever
// ticket-id list the caller supplies (the filtered+sorted list the user came from,
// or the system-default fallback) — never a re-derived or re-sorted order.
export function adjacentTicketIds(ticketIds, currentId) {
  const index = ticketIds.indexOf(currentId);
  return {
    prevTicketId: index > 0 ? ticketIds[index - 1] : null,
    nextTicketId: index >= 0 && index < ticketIds.length - 1 ? ticketIds[index + 1] : null,
  };
}

// A conservative, string-only check for whether an email's HTML body has any content
// actually worth rendering — as opposed to surviving-but-empty markup (a bare wrapper
// div, a lone <br>, an &nbsp;, or just a stripped style/meta block). Deliberately never
// touches the DOM/dangerouslySetInnerHTML: this only decides which branch to render,
// SecureEmailHtml still does the real sanitization when the HTML path is chosen.
export function hasMeaningfulEmailHtml(html) {
  if (!html || typeof html !== "string") return false;

  // Non-visual blocks never count as content, and stripping them first keeps their
  // contents (e.g. CSS text) from being mistaken for visible text below.
  let working = html.replace(/<(script|style|head|meta)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "");
  working = working.replace(/<meta\b[^>]*\/?>/gi, "");

  // An <img> is content on its own (inline/cid images included) — no need to also
  // check tables/links specially below, since their own text (if any) already
  // survives the tag-stripping pass and is caught by the final text check.
  if (/<img\b/i.test(working)) return true;

  const visibleText = working
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;|&#160;|&#xa0;/gi, " ")
    .replace(/&zwnj;|&#8203;|&#x200b;/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  return visibleText.length > 0;
}

const hasEmailSignature = (body = "") => body.includes('data-prodigy-signature="true"');

const TICKET_SUBJECT_PREFIX_RE = /^קריאה\s*#\d+\s*[—-]\s*/u;

const cleanEmailSubject = (subject = "") =>
  subject.replace(TICKET_SUBJECT_PREFIX_RE, "").trim();

// The bell links to /tickets/<id>?tab=ai-diagnosis. When that ticket is ALREADY open the page is not remounted
// (same id), and the tab was read from the URL only once, so the click closed the panel and did nothing. Follow every
// navigation (location.key changes even for the same URL) that carries a tab.
export function useTabFromNavigation(setActiveTab) {
  const location = useLocation();
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab) setActiveTab(tab);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
}

// While TYPING, only the "קריאה #N —" prefix is dropped: trimming every keystroke reverted a typed trailing space,
// so words could not be separated at the end of the subject. The subject is trimmed once, when it is sent.
export const stripTicketSubjectPrefix = (subject = "") => subject.replace(TICKET_SUBJECT_PREFIX_RE, "");

// The composer opens with the Prodigy signature pre-filled, so isRichTextEmpty (which counts the signature's text)
// called an untouched form "not empty" and enabled Send. A body that is only the signature is not a message.
// Enter in a text input of the e-mail composer (To / CC / Subject) submitted the form, i.e. SENT the customer email with
// no click on the labelled send button. Enter on a button (the send button itself) still works.
export const blockEnterSubmit = (event) => {
  if (event.key === "Enter" && event.target?.tagName === "INPUT") event.preventDefault();
};

// Which of the locally picked (pending) status / technician actually differ from what the server has.
export function dirtyPendingChanges(ticket, pendingStatus, pendingTechId) {
  return {
    statusDirty: pendingStatus !== undefined && pendingStatus !== ticket.status,
    techDirty: pendingTechId !== undefined && (pendingTechId || null) !== (ticket.technician_id || null),
  };
}

// Which pending picks a finished update covered: only what that request carried (the resolution-note save and the
// close dialog carry neither the pending status nor the pending technician).
export function pendingCoveredBy(variables) {
  return { status: Boolean(variables) && "status" in variables, technician: Boolean(variables) && "technician_id" in variables };
}

export function isEmailBodyEmpty(html = "") {
  const withoutSignature = html.replace(/<div[^>]*data-prodigy-signature="true"[^>]*>[\s\S]*?<\/div>/i, "");
  return isRichTextEmpty(withoutSignature);
}

// The To input is type="text" (an email input strips and re-validates the address list, which broke comma / Hebrew
// pasted lists), so `required` accepts "  ": the backend then falls back to the ticket's sender and the technician's
// blank field silently became a send to somebody. A blank To, subject or body is not a sendable message.
export function canSendCustomerEmail({ to = "", subject = "", body = "" } = {}) {
  return Boolean(to.trim()) && Boolean(subject.trim()) && !isEmailBodyEmpty(body);
}

// A failed load of a ticket tab is not "nothing here": an empty conversation next to a working composer lets the technician
// reply without seeing what the customer wrote.
function LoadFailed({ message, onRetry }) {
  return (
    <div role="alert" className="flex items-center gap-3 text-sm text-red-600">
      <span>{message}</span>
      <button type="button" onClick={() => onRetry()} className="underline">נסה שוב</button>
    </div>
  );
}

// Compact Digital-Employee strip on the ticket screen: which employee works this ticket, the
// current run in human wording (with a highlighted review/approval wait), the last finished runs
// and the conversations bound to the ticket. Every piece links out to its full page (employee,
// run, conversation) — the panel itself is a bounded summary from the read-only
// /employee-activity projection, never run context, analysis dumps or runtime logs.
export function TicketEmployeeActivityPanel({ ticketId, canView }) {
  const { data, isError, refetch } = useQuery({
    queryKey: ["employee-activity", ticketId],
    queryFn: () => fetchTicketEmployeeActivity(ticketId),
    enabled: Boolean(canView && ticketId),
    staleTime: 15000,
    // A live run moves between statuses within the poll interval; an all-finished ticket is static.
    refetchInterval: (query) => (query.state.data?.current_run ? 15000 : false),
  });

  if (!canView) return null;
  if (isError && !data) {
    return (
      <div role="status" className="bg-white rounded-xl shadow-sm border px-3 xl:px-4 py-2 flex items-center gap-3 text-xs text-gray-500">
        <span>לא ניתן לטעון את נתוני העובד הדיגיטלי</span>
        <button type="button" onClick={() => refetch()} className="text-brand-600 hover:underline">נסה שוב</button>
      </div>
    );
  }
  if (!data) return null;

  const employee = data.employee;
  const currentRun = data.current_run;
  const recentRuns = Array.isArray(data.recent_runs) ? data.recent_runs.slice(0, 3) : [];
  const conversations = Array.isArray(data.conversations) ? data.conversations : [];
  if (!employee && !currentRun && recentRuns.length === 0 && conversations.length === 0) return null;

  const waitLabel = EMPLOYEE_ACTIVITY_WAIT_LABELS[data.waiting_for] || null;
  const currentAgo = currentRun?.started_at ? employeeActivityTimeAgo(currentRun.started_at) : "";
  const latestConversation = conversations[0];
  const latestConversationStatus = latestConversation
    ? conversationStatusMeta(latestConversation.status)
    : null;

  return (
    <section aria-label="עובד דיגיטלי" className="bg-white rounded-xl shadow-sm border px-3 xl:px-4 py-2.5 xl:py-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        {employee && (
          <>
            <EmployeeAvatar name={employee.name} avatarKey={employee.avatar_key} avatarColor={employee.avatar_color} size="sm" />
            <span className="text-xs text-gray-400">עובד דיגיטלי:</span>
            <Link to={`/agents/${employee.agent_id}`} className="font-semibold text-gray-800 hover:text-brand-600 hover:underline">
              {employee.name}
            </Link>
            {employee.role_title && <span className="text-xs text-gray-500">{employee.role_title}</span>}
            {employee.is_enabled === false && (
              <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">מושבת</span>
            )}
          </>
        )}
        {currentRun && (
          waitLabel ? (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200">⏳ {waitLabel}</span>
          ) : (
            <span className="font-medium text-violet-700">
              {employeeActivityStatusText(currentRun.status)}
              {currentAgo && <span className="text-xs font-normal text-gray-400"> · {currentAgo}</span>}
            </span>
          )
        )}
        <span className="flex-1" />
        {currentRun && (
          <Link
            to={`/agent-runs/${currentRun.run_id}`}
            className="text-xs font-medium text-brand-600 hover:underline"
            aria-label={`פתח ריצה נוכחית — ${employeeActivityStatusText(currentRun.status)}`}
          >
            פתח ריצה נוכחית
          </Link>
        )}
        {latestConversation && (
          <Link to={`/conversations/${latestConversation.conversation_id}`} className="text-xs font-medium text-brand-600 hover:underline">
            פתח שיחה
          </Link>
        )}
      </div>

      {recentRuns.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-gray-400">עבודה אחרונה</p>
          <ul className="space-y-0.5">
            {recentRuns.map((run) => {
              const ago = employeeActivityTimeAgo(run.finished_at || run.updated_at || run.started_at);
              const summary = run.result_summary || run.likely_issue || "";
              const statusLabel = employeeActivityStatusText(run.status);
              // A degraded run's summary is the fallback adapter's own scaffolding, not a finding
              // about this ticket, so it is qualified before the words it qualifies.
              const degradedLabel = degradedAnalysisLabel(run.degraded);
              const outcomeColor = ["failed", "rejected"].includes(run.status)
                ? "text-red-600"
                : ["completed", "resolved", "approved"].includes(run.status)
                  ? "text-green-600"
                  : "text-gray-500";
              return (
                <li key={run.run_id}>
                  <Link
                    to={`/agent-runs/${run.run_id}`}
                    className="flex items-center gap-2 text-xs rounded-lg px-2 py-1 -mx-2 hover:bg-gray-50 transition-colors"
                    aria-label={`פתח ריצה — ${statusLabel}${ago ? `, ${ago}` : ""}${summary ? `, ${summary}` : ""}${degradedLabel ? `, ${degradedLabel}` : ""}`}
                  >
                    <span className={`shrink-0 font-medium ${outcomeColor}`}>{statusLabel}</span>
                    {ago && <span className="shrink-0 text-gray-400">{ago}</span>}
                    {degradedLabel && (
                      <span
                        data-testid="employee-activity-degraded"
                        className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                      >
                        {degradedLabel}
                      </span>
                    )}
                    {summary && <span className="truncate text-gray-600">{summary}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {latestConversation && (
        <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600">
          <span className="text-gray-400">שיחה עם הלקוח:</span>
          {latestConversationStatus && (
            <span className={`font-medium px-1.5 py-0.5 rounded-full ${latestConversationStatus.badge}`}>{latestConversationStatus.label}</span>
          )}
          {latestConversation.updated_at && (
            <span className="text-gray-400">עודכנה {employeeActivityTimeAgo(latestConversation.updated_at)}</span>
          )}
          {conversations.length > 1 && <span className="text-gray-400">· {conversations.length - 1} שיחות נוספות</span>}
        </div>
      )}
    </section>
  );
}

// The ticket's own employee binding (GET/PUT /api/tickets/{id}/employee-assignment): who is
// assigned, a direct link into that employee's workspace, and the assign/reassign/clear picker.
// The read gates on agents_view and the write on agents_run — the same split the backend
// enforces. A change is its own immediate, audited action: it never rides along in the
// status/technician "save changes" payload, so human technician assignment stays untouched.

export function fetchTicketEmployeeAssignment(ticketId) {
  return api.get(`/api/tickets/${ticketId}/employee-assignment`).then((r) => r.data);
}

export function saveTicketEmployeeAssignment(ticketId, agentId) {
  // Explicit null is the backend's clear; the field is always present so a truncated payload
  // can never silently clear an existing binding.
  return api.put(`/api/tickets/${ticketId}/employee-assignment`, { agent_id: agentId ?? null }).then((r) => r.data);
}

// Picker options: the assignable agents, plus the currently assigned employee even if they no
// longer qualify (disabled, or now out of the customer's scope) — without that the select would
// show blank while the row right above still shows them as assigned.
export function employeeAssignmentOptions(selectableAgents, currentEmployee) {
  const options = (selectableAgents || []).map((agent) => ({ id: agent.id, name: agent.name }));
  if (currentEmployee && !options.some((option) => option.id === currentEmployee.agent_id)) {
    options.unshift({ id: currentEmployee.agent_id, name: currentEmployee.name });
  }
  return options;
}

export function TicketEmployeeAssignmentPanel({ ticketId, companyId, canView, canAssign }) {
  const qc = useQueryClient();
  const [saveFailed, setSaveFailed] = useState(null);
  const { data: assignment, isError, refetch } = useQuery({
    queryKey: ["employee-assignment", ticketId],
    queryFn: () => fetchTicketEmployeeAssignment(ticketId),
    enabled: Boolean(canView && ticketId),
    staleTime: 15000,
  });
  const { data: agents = [] } = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentsApi.list(),
    enabled: Boolean(canView && canAssign),
    staleTime: 30000,
  });

  const saveAssignment = useMutation({
    mutationFn: (agentId) => saveTicketEmployeeAssignment(ticketId, agentId),
    onSuccess: (updated) => {
      setSaveFailed(null);
      // The PUT response is the new assignment; seeding the cache avoids a refetch flash in the
      // select. The change also lands in the ticket's activity log as an audit event.
      qc.setQueryData(["employee-assignment", ticketId], updated);
      qc.invalidateQueries({ queryKey: ["history", ticketId] });
    },
    onError: (err) => setSaveFailed(detailText(err?.response?.data?.detail, "שגיאה בעדכון שיבוץ העובד הדיגיטלי")),
  });

  if (!canView) return null;
  if (isError && !assignment) {
    return (
      <div role="status" className="bg-white rounded-xl shadow-sm border px-3 xl:px-4 py-2 flex items-center gap-3 text-xs text-gray-500">
        <span>לא ניתן לטעון את שיבוץ העובד הדיגיטלי</span>
        <button type="button" onClick={() => refetch()} className="text-brand-600 hover:underline">נסה שוב</button>
      </div>
    );
  }
  if (!assignment) return null;

  const employee = assignment.employee || null;
  // Only agents that are enabled and may work this ticket's customer — the backend refuses the
  // rest (422), so the picker does not offer them.
  const selectableAgents = canAssign ? agentsForTicket(agents.filter((agent) => agent.is_enabled), companyId) : [];
  const options = employeeAssignmentOptions(selectableAgents, employee);

  return (
    <section aria-label="שיבוץ עובד דיגיטלי" className="bg-white rounded-xl shadow-sm border px-3 xl:px-4 py-2.5 xl:py-3">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        {employee && <EmployeeAvatar name={employee.name} avatarKey={employee.avatar_key} avatarColor={employee.avatar_color} size="sm" />}
        <span className="text-xs text-gray-400">עובד דיגיטלי:</span>
        {employee ? (
          <>
            <Link to={`/agents/${employee.agent_id}`} className="font-semibold text-gray-800 hover:text-brand-600 hover:underline">
              {employee.name}
            </Link>
            {employee.role_title && <span className="text-xs text-gray-500">{employee.role_title}</span>}
            {employee.is_enabled === false && (
              <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">מושבת</span>
            )}
          </>
        ) : (
          <span className="text-gray-500">לא שובץ עובד דיגיטלי</span>
        )}
        <span className="flex-1" />
        {canAssign && saveAssignment.isPending && <span className="text-xs text-gray-400">מעדכן שיבוץ...</span>}
        {canAssign && (
          <select
            aria-label="בחירת עובד דיגיטלי"
            value={employee ? employee.agent_id : ""}
            onChange={(e) => { setSaveFailed(null); saveAssignment.mutate(e.target.value || null); }}
            disabled={saveAssignment.isPending}
            className="border rounded-lg px-3 py-1.5 text-sm disabled:opacity-60"
          >
            <option value="">ללא עובד דיגיטלי</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
        )}
      </div>
      {canAssign && saveFailed && (
        <p role="alert" className="text-xs text-red-600 mt-1.5">{saveFailed}</p>
      )}
    </section>
  );
}

function TicketDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { can: canAgent } = useAgentPermissions();
  const { addNotification } = useNotification();

  // Where to return to when leaving this ticket — carried in via ?from= so it
  // survives a refresh or a direct link; unknown origin falls back to the dashboard.
  const backTo = searchParams.get("from") || "/dashboard";

  // Previous/Next navigation: the ordered ticket id list from the list view the user
  // came from (Dashboard/TicketsPage pass it as navigation state when opening a ticket).
  // Without it (direct link, refresh, or an entry point that doesn't carry context yet),
  // fall back to the system's default ticket order via the same list endpoint those
  // pages already use.
  const navTicketIds = location.state?.ticketIds;
  const hasNavContext = Array.isArray(navTicketIds) && navTicketIds.length > 0;
  const { data: fallbackTickets } = useQuery({
    queryKey: ["tickets-nav-fallback"],
    queryFn: () => ticketsApi.list({ limit: 2000 }),
    enabled: !hasNavContext,
    staleTime: 60_000,
  });
  const navIds = hasNavContext ? navTicketIds : (fallbackTickets || []).map((t) => t.id);
  const { prevTicketId, nextTicketId } = adjacentTicketIds(navIds, id);
  const goToAdjacentTicket = (targetId) => {
    if (!targetId) return;
    const qs = searchParams.toString();
    navigate(`/tickets/${targetId}${qs ? `?${qs}` : ""}`, { state: { ticketIds: navIds } });
  };

  const [aiAnalyzing, setAiAnalyzing]       = useState(false);
  const [quickApproving, setQuickApproving] = useState(false);
  const [quickRejecting, setQuickRejecting] = useState(false);
  const [comment, setComment]               = useState("");
  const [commentBodyError, setCommentBodyError] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [draftCommentBody, setDraftCommentBody] = useState("");
  const [commentEditError, setCommentEditError] = useState(null);
  const [additionalTechnicianIds, setAdditionalTechnicianIds] = useState([]);
  const [pendingStatus, setPendingStatus]   = useState(undefined);
  const [pendingTechId, setPendingTechId]   = useState(undefined);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeResolutionNote, setCloseResolutionNote] = useState("");
  const [draftResolutionNote, setDraftResolutionNote] = useState("");
  const [saveAsKnowledge, setSaveAsKnowledge] = useState(false);
  const pendingKbData = useRef(null);
  const autoRecipientsRef = useRef({ to: "", cc: "" });   // the To/CC the composer was last given automatically
  const [savedMsg, setSavedMsg]             = useState(false);
  // Four independent save actions (status/technician, comment edit, sender edit, contact-from-email)
  // all flash this same "✓ Saved" flag -- triggering two within 3s used to have the FIRST action's
  // timer clear the SECOND action's still-fresh indicator early.
  const savedMsgTimer = useRef(null);
  const flashSavedMsg = () => {
    setSavedMsg(true);
    clearTimeout(savedMsgTimer.current);
    savedMsgTimer.current = setTimeout(() => setSavedMsg(false), 3000);
  };
  const [isEditingTitle, setIsEditingTitle]     = useState(false);
  const [draftTitle, setDraftTitle]             = useState("");
  const [isEditingBody, setIsEditingBody]       = useState(false);
  const [draftBody, setDraftBody]               = useState("");
  const [isEditingSender, setIsEditingSender]    = useState(false);
  const [draftSenderName, setDraftSenderName]    = useState("");
  const [draftSenderEmail, setDraftSenderEmail]  = useState("");
  const [senderSaveError, setSenderSaveError]    = useState(null);
  const [senderEditTab, setSenderEditTab]        = useState("manual"); // "manual" | "contacts"
  const [senderContactQuery, setSenderContactQuery]     = useState("");
  const [senderContactResults, setSenderContactResults] = useState([]);
  const [senderContactLoading, setSenderContactLoading] = useState(false);
  const [senderContactError, setSenderContactError]     = useState(null);
  const [showAddSenderContact, setShowAddSenderContact] = useState(false);
  const [addContactDraft, setAddContactDraft]    = useState({ first_name: "", last_name: "", email: "", phone: "", company_id: "" });
  const [addContactError, setAddContactError]    = useState(null);
  const [duplicateContact, setDuplicateContact]  = useState(null);
  const [contactCreationContext, setContactCreationContext] = useState("ticket-sender");
  const [localCompany, setLocalCompany]         = useState(undefined); // undefined = use ticket.company
  const [companySaveError, setCompanySaveError] = useState(null);
  const [showAddCompanyForm, setShowAddCompanyForm] = useState(false);
  const [newCompanyName, setNewCompanyName]     = useState("");
  const [newCompanyDomain, setNewCompanyDomain] = useState("");
  const [newCompanyError, setNewCompanyError]   = useState(null);
  const [showEmail, setShowEmail]           = useState(false);
  const [aiSolution, setAiSolution]         = useState(null);
  const [showAiPanel, setShowAiPanel]       = useState(false);
  const [showSpPanel, setShowSpPanel]       = useState(false);
  const [spQuery, setSpQuery]               = useState("");
  const [spFolderId, setSpFolderId]         = useState("");
  const [emailTo, setEmailTo]               = useState("");
  const [emailCc, setEmailCc]               = useState("");
  const [emailSubject, setEmailSubject]     = useState("");
  const [emailBody, setEmailBody]           = useState("");
  const [emailBodyError, setEmailBodyError] = useState(null);
  const [emailFiles, setEmailFiles]         = useState([]);
  const [includeEmailThread, setIncludeEmailThread] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactSearch, setContactSearch]         = useState("");
  const [contacts, setContacts]                   = useState([]);
  const [contactsLoading, setContactsLoading]     = useState(false);
  const [commentFiles, setCommentFiles]     = useState([]);
  const [isCommentImageProcessing, setIsCommentImageProcessing] = useState(false);
  const commentSubmitLockRef = useRef(false);
  const [showMoveQueue, setShowMoveQueue]   = useState(false);
  const [moveQueueTarget, setMoveQueueTarget] = useState("project"); // "project" | "maintenance"
  const [moveProjectId, setMoveProjectId]   = useState("");
  const [moveCategory, setMoveCategory]     = useState("backups");
  const [activeTab, setActiveTab]           = useState(searchParams.get("tab") || "email-thread");
  useTabFromNavigation(setActiveTab);
  const [splitEmailTarget, setSplitEmailTarget] = useState(null); // the email message being split, or null when the modal is closed
  const [splitEmailTitle, setSplitEmailTitle]   = useState("");
  const [splitEmailTechnicianId, setSplitEmailTechnicianId] = useState("");
  const [splitEmailResult, setSplitEmailResult] = useState(null); // the newly created ticket, once the split succeeds
  const [splitEmailError, setSplitEmailError]   = useState(null);

  const { data: ticket, isLoading, isError: ticketFailed, error: ticketError, refetch: refetchTicket } = useQuery({
    queryKey: ["ticket", id],
    queryFn: () => ticketsApi.get(id),
  });
  const { data: comments = [], isError: commentsFailed, refetch: refetchComments } = useQuery({
    queryKey: ["comments", id],
    queryFn: () => commentsApi.list(id),
  });
  const { data: technicians = [] } = useQuery({
    queryKey: ["technicians"],
    queryFn: () => techniciansApi.list(),
  });
  const myTechnicianId = technicians.find(
    (t) => t.email?.toLowerCase() === user?.email?.toLowerCase()
  )?.id ?? null;
  const { data: companies = [] } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
  });
  const { data: history = [], isError: historyFailed, refetch: refetchHistory } = useQuery({
    queryKey: ["history", id],
    queryFn: () => historyApi.list(id),
  });
  const {
    data: ticketAgentRuns = [],
    isError: agentRunsFailed,
    isLoading: agentRunsLoading,
    dataUpdatedAt: agentRunsUpdatedAt,
  } = useQuery({
    queryKey: ["agentRuns", id],
    queryFn: () => agentRunsApi.list(id),
    enabled: canAgent("agents_view"),
    staleTime: 15000,
    refetchInterval: (query) => {
      const runs = query.state.data ?? [];
      const hasActive = runs.some((r) => AI_BLOCKING_STATUSES.has(r.status));
      return hasActive ? 10000 : false;
    },
  });
  useQuery({
    queryKey: ["ai-settings"],
    queryFn: () => aiApi.getSettings(),
    enabled: ["admin", "owner"].includes(user?.role),
    staleTime: 60000,
  });
  const { data: assigneeSuggestion } = useQuery({
    queryKey: ["suggest-assignee", id],
    queryFn: () => ticketsApi.suggestAssignee(id).then((r) => r.suggestion),
    enabled: !!ticket?.ai_category && !ticket?.technician_id,
    staleTime: 120_000,
  });
  // The ticket's Digital Employee binding. Same query key as TicketEmployeeAssignmentPanel below, so
  // the two observers share one fetch. It is what decides which employee starts this ticket's work:
  // without that, the assignment was recorded and drawn but never consumed, and the run went to the
  // backend's default agent while this screen named someone else.
  const { data: employeeAssignment } = useQuery({
    queryKey: ["employee-assignment", id],
    queryFn: () => fetchTicketEmployeeAssignment(id),
    enabled: canAgent("agents_view"),
    staleTime: 15000,
  });
  const assignedAgentId = assignedStartAgentId(employeeAssignment);
  const { data: emailThread = [], isError: emailThreadFailed, refetch: refetchEmailThread } = useQuery({
    queryKey: ["email-thread", id],
    queryFn: () => emailThreadApi.list(id),
  });
  const { data: savedContacts = [], isLoading: savedContactsLoading } = useQuery({
    queryKey: ["contacts", "email-thread-senders"],
    queryFn: () => contactsApi.list(),
    enabled: activeTab === "email-thread",
  });
  const { data: ticketAttachments = [], isLoading: isLoadingAttachments, isError: attachmentsFailed } = useQuery({
    queryKey: ["ticket-attachments", id],
    queryFn: () => attachmentsApi.list(id),
    // Also needed on the comments tab: pasted-image comments render their
    // inline attachment through the same authenticated blob mechanism as
    // the email thread, which requires the attachment list to resolve
    // content types. And whenever the description itself is rich HTML: a pasted/inline image there resolves
    // through the same list, so on the default tab it rendered "cannot display the image" until another tab
    // had been opened.
    enabled: activeTab === "attachments" || activeTab === "comments" || Boolean(ticket?.description_html),
  });
  const { data: projectsList = [] } = useQuery({
    queryKey: ["projects-list-brief"],
    // open_only: a closed project is not a sensible "attach this ticket" target, and
    // excluding it server-side (before the limit) keeps the truncation window full of
    // genuinely active candidates instead of silently dropping one beyond the newest 500.
    queryFn: () => projectsApi.list({ limit: 500, open_only: true }),
    enabled: showMoveQueue && moveQueueTarget === "project",
    staleTime: 60_000,
  });

  const { data: linkedProject } = useQuery({
    queryKey: ["project", ticket?.linked_project_id],
    queryFn: () => projectsApi.get(ticket.linked_project_id),
    enabled: !!ticket?.linked_project_id,
    staleTime: 30_000,
  });

  const { data: linkedMaintItem } = useQuery({
    queryKey: ["maintenance", ticket?.linked_maintenance_id],
    queryFn: () => maintenanceApi.get(ticket.linked_maintenance_id),
    enabled: !!ticket?.linked_maintenance_id,
    staleTime: 30_000,
  });

  const [expandedEmails, setExpandedEmails] = useState(new Set());
  const toggleEmail = (mid) =>
    setExpandedEmails((prev) => {
      const next = new Set(prev);
      next.has(mid) ? next.delete(mid) : next.add(mid);
      return next;
    });

  const updateTicket = useMutation({
    mutationFn: (data) => ticketsApi.update(id, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      // Same dashboard/queue keys moveQueueMutation and addComment already invalidate below: this
      // mutation is the one that actually changes status/technician_id, the fields those views group
      // and filter by.
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      // Clear only what THIS request saved: the resolution-note "save" and the close dialog send neither the pending
      // status nor the pending technician, and used to discard them while reporting success.
      const covered = pendingCoveredBy(variables);
      if (covered.status) setPendingStatus(undefined);
      if (covered.technician) setPendingTechId(undefined);
      flashSavedMsg();
      if (variables?.status === "closed" && pendingKbData.current) {
        const kb = pendingKbData.current;
        pendingKbData.current = null;
        // Scoped to this ticket's customer: an org-wide article (no company_id) is quoted into EVERY customer's AI
        // prompts once approved, and this text is a resolution note about one customer's environment.
        const kbCompanyId = localCompany !== undefined ? localCompany?.id : ticket?.company?.id;
        knowledgeApi.create({ title: kb.title, problem_pattern: kb.problem_pattern, solution: kb.solution, tags: "ticket-resolution", ...(kbCompanyId ? { company_id: kbCompanyId } : {}) })
          .then(() => addNotification("מאמר ידע נשלח לאישור מנהל", "success", "/knowledge"))
          .catch((err) => addNotification(`הקריאה נסגרה, אך מאמר הידע לא נשמר: ${detailText(err?.response?.data?.detail, "שגיאה בשמירת מאמר ידע")}`, "error"));
      }
    },
    onError: (err) => {
      pendingKbData.current = null;   // a failed close must not leave a knowledge article queued for the next save
      addNotification(err?.response?.data?.detail || "שגיאה בשמירת הקריאה", "error");
    },
  });

  // The close-ticket modal had no Escape handler -- a keyboard user could tab through the whole
  // page behind the backdrop with no way to dismiss it short of clicking the (unlabeled) ✕.
  useEffect(() => {
    if (!showCloseModal) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") { updateTicket.reset(); setShowCloseModal(false); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showCloseModal, updateTicket]);

  const deleteTicket = useMutation({
    mutationFn: () => ticketsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tickets"] });
      navigate("/tickets");
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת הקריאה", "error"),
  });

  const moveQueueMutation = useMutation({
    mutationFn: (data) => ticketsApi.moveQueue(id, data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      setShowMoveQueue(false);
      if (updated.linked_project_id) {
        navigate(`/projects/${updated.linked_project_id}`);
      } else if (updated.linked_maintenance_id) {
        navigate(`/maintenance/${updated.linked_maintenance_id}`);
      }
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בהעברת הקריאה", "error"),
  });

  // The move-queue dialog had no Escape handler -- a keyboard user had to find the unlabeled ✕.
  useEffect(() => {
    if (!showMoveQueue) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") { moveQueueMutation.reset(); setShowMoveQueue(false); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showMoveQueue, moveQueueMutation]);

  const addComment = useMutation({
    mutationFn: ({ body, is_internal, additional_technician_id, additional_technician_ids, files }) =>
      commentsApi.create(id, { body, is_internal, additional_technician_id, additional_technician_ids, files }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comments", id] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      qc.invalidateQueries({ queryKey: ["ticket-attachments", id] });
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      setComment("");
      setAdditionalTechnicianIds([]);
      setCommentFiles([]);
      setCommentBodyError(null);
    },
    onError: (error) => {
      setCommentBodyError(error?.response?.data?.detail || "שגיאה בהוספת ההערה");
    },
  });

  const updateComment = useMutation({
    mutationFn: ({ commentId, body }) => commentsApi.update(id, commentId, { body }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["comments", id] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      // editingCommentId is a single shared slot: switching to edit a different comment while
      // this save is still in flight otherwise let this resolution close THAT comment's
      // still-open, unsaved editor early and flash a "saved" message that looks like it confirms
      // the wrong comment's save.
      setEditingCommentId((current) => (current === variables.commentId ? null : current));
      setCommentEditError(null);
      flashSavedMsg();
    },
    onError: (error) => {
      setCommentEditError(error?.response?.data?.detail || "שגיאה בשמירת ההערה");
    },
  });

  const deleteComment = useMutation({
    mutationFn: (commentId) => commentsApi.delete(id, commentId),
    onSuccess: (_data, commentId) => {
      qc.setQueryData(["comments", id], (comments) => comments?.filter((comment) => comment.id !== commentId));
      qc.invalidateQueries({ queryKey: ["history", id] });
      addNotification("ההערה נמחקה בהצלחה");
    },
    onError: (error) => {
      addNotification(error?.response?.data?.detail || "שגיאה במחיקת ההערה", "error");
    },
  });

  const startEditComment = (c) => {
    setEditingCommentId(c.id);
    setDraftCommentBody(c.body_html || plainTextToEditorHtml(c.body));
    setCommentEditError(null);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setCommentEditError(null);
  };

  const saveEditComment = () => {
    if (isRichTextEmpty(draftCommentBody)) {
      setCommentEditError("לא ניתן לשמור הערה ריקה");
      return;
    }
    updateComment.mutate({ commentId: editingCommentId, body: draftCommentBody });
  };

  const canEditComment = (c) =>
    c.is_internal &&
    c.source !== "email_inbound" &&
    (c.author_id === user?.id || ["owner", "admin", "technician"].includes(user?.role));

  const canDeleteComment = (c) => isDeletableInternalComment(c, user);

  const confirmDeleteComment = (commentId) => {
    if (deleteComment.isPending) return;
    confirmInternalCommentDeletion(commentId, window.confirm, deleteComment.mutate);
  };

  const sendEmail = useMutation({
    mutationFn: ({ to, cc, subject, body, includeEmailThread, files }) =>
      emailApi.send(id, { to, cc, subject, body, include_email_thread: includeEmailThread, files }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comments", id] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      qc.invalidateQueries({ queryKey: ["email-thread", id] });
      qc.invalidateQueries({ queryKey: ["ticket-attachments", id] });
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      setShowEmail(false);
      setEmailTo("");
      setEmailCc("");
      setEmailSubject("");
      setEmailBody("");
      setEmailFiles([]);
      setIncludeEmailThread(false);
      setEmailBodyError(null);
      addNotification("מייל נשלח בהצלחה ✓", "success");
    },
    onError: (error) => {
      setEmailBodyError(error?.response?.data?.detail || "שגיאה בשליחת המייל");
    },
  });

  const notifyTechnician = useMutation({
    mutationFn: () => emailApi.notifyTechnician(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["history", id] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשליחת הודעה לטכנאי", "error"),
  });

  const updateCompany = useMutation({
    mutationFn: (companyId) => ticketsApi.update(id, { company_id: companyId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      setLocalCompany(undefined);
      setCompanySaveError(null);
    },
    // A 422 (bad e-mail, too long a name) used to read "network error" and drop the reason the server gave.
    onError: (err) => {
      setLocalCompany(undefined);
      setCompanySaveError(err?.response ? detailText(err.response.data?.detail, "שגיאה בשמירה") : "שגיאת רשת — לא ניתן לשמור");
    },
  });

  const updateTitle = useMutation({
    mutationFn: (title) => ticketsApi.update(id, { title }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      setIsEditingTitle(false);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשמירת הכותרת", "error"),
  });

  const updateSender = useMutation({
    mutationFn: (data) => ticketsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      setIsEditingSender(false);
      setSenderSaveError(null);
      flashSavedMsg();
    },
    onError: (err) => {
      setSenderSaveError(err?.response ? detailText(err.response.data?.detail, "שגיאה בשמירה") : "שגיאת רשת — לא ניתן לשמור");
    },
  });

  const updateBody = useMutation({
    mutationFn: (description) => ticketsApi.update(id, { description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      setIsEditingBody(false);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשמירת תיאור הקריאה", "error"),
  });
  const startEditBody = () => {
    setDraftBody(ticket?.description || "");
    setIsEditingBody(true);
  };
  const cancelEditBody = () => {
    setIsEditingBody(false);
    setDraftBody("");
  };
  const saveBody = () => {
    if (updateBody.isPending) return;
    updateBody.mutate(draftBody);
  };

  const splitFromEmail = useMutation({
    mutationFn: (data) => ticketsApi.split(data),
    onSuccess: (newTicket) => {
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["history", id] });
      // the split adds an internal comment ("split to #N") to the source ticket
      qc.invalidateQueries({ queryKey: ["comments", id] });
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      addNotification(`קריאה #${newTicket.ticket_number} נוצרה בהצלחה מהמייל`, "success", `/tickets/${newTicket.id}`);
      setSplitEmailResult(newTicket);
      setSplitEmailError(null);
    },
    onError: (err) => {
      setSplitEmailError(err?.response?.data?.detail || "שגיאה ביצירת הקריאה החדשה");
    },
  });
  const openSplitEmailModal = (msg) => {
    setSplitEmailTarget(msg);
    setSplitEmailTitle(msg.subject || "");
    setSplitEmailTechnicianId("");
    setSplitEmailResult(null);
    setSplitEmailError(null);
  };
  const closeSplitEmailModal = () => {
    setSplitEmailTarget(null);
    setSplitEmailTitle("");
    setSplitEmailTechnicianId("");
    setSplitEmailResult(null);
    setSplitEmailError(null);
  };
  // The split-email dialog had no Escape handler -- a keyboard user had to find the unlabeled ✕.
  useEffect(() => {
    if (!splitEmailTarget) return;
    const onKeyDown = (e) => { if (e.key === "Escape") closeSplitEmailModal(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [splitEmailTarget]);
  const confirmSplitEmail = () => {
    if (splitFromEmail.isPending || !splitEmailTarget) return;
    splitFromEmail.mutate({
      source_ticket_id: id,
      new_subject: splitEmailTitle.trim() || splitEmailTarget.subject || "",
      note: "",
      technician_id: splitEmailTechnicianId || null,
      email_message_id: splitEmailTarget.id,
    });
  };

  const createCompany = useMutation({
    mutationFn: (data) => companiesApi.create(data),
    onSuccess: (newCompany) => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      setLocalCompany(newCompany);
      updateCompany.mutate(newCompany.id);
      setShowAddCompanyForm(false);
      setNewCompanyName("");
      setNewCompanyDomain("");
      setNewCompanyError(null);
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail;
      setNewCompanyError(detail || "שגיאה ביצירת החברה");
    },
  });

  const handleAddCompany = () => {
    const name   = newCompanyName.trim();
    const domain = newCompanyDomain.trim();
    if (!name)   { setNewCompanyError("שם חברה הוא שדה חובה"); return; }
    if (!domain) { setNewCompanyError("דומיין הוא שדה חובה"); return; }
    if (domain.includes("@") || domain.includes(" ")) {
      setNewCompanyError("דומיין לא תקין — אל תכלול @ או רווחים");
      return;
    }
    setNewCompanyError(null);
    createCompany.mutate({ name, domain });
  };

  const startEditSender = () => {
    setDraftSenderName(ticket?.sender_name ?? "");
    setDraftSenderEmail(ticket?.sender_email ?? "");
    setSenderSaveError(null);
    setSenderEditTab("manual");
    setSenderContactQuery("");
    setSenderContactResults([]);
    setIsEditingSender(true);
  };

  const cancelEditSender = () => {
    setIsEditingSender(false);
    setSenderSaveError(null);
    setDraftSenderName(ticket?.sender_name ?? "");
    setDraftSenderEmail(ticket?.sender_email ?? "");
  };

  const handleSaveSender = () => {
    const name = draftSenderName.trim();
    const email = draftSenderEmail.trim();
    if (!name) {
      setSenderSaveError("שם שולח הוא שדה חובה");
      return;
    }
    if (!email) {
      setSenderSaveError("מייל שולח הוא שדה חובה");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSenderSaveError("מייל שולח לא תקין");
      return;
    }
    if (name === (ticket?.sender_name ?? "").trim() && email.toLowerCase() === (ticket?.sender_email ?? "").trim().toLowerCase()) {
      setIsEditingSender(false);
      setSenderSaveError(null);
      return;
    }
    setSenderSaveError(null);
    updateSender.mutate({ sender_name: name, sender_email: email });
  };

  useEffect(() => {
    setDraftResolutionNote(ticket?.resolution_note || "");
  }, [ticket?.resolution_note]);

  useEffect(() => {
    if (!isEditingSender || senderEditTab !== "contacts") return;
    let active = true;
    setSenderContactLoading(true);
    setSenderContactError(null);
    const term = senderContactQuery.trim();
    const timer = setTimeout(() => {
      contactsApi
        .list(term ? { search: term } : {})
        .then((result) => { if (active) setSenderContactResults(result || []); })
        .catch(() => { if (active) setSenderContactError("שגיאה בטעינת אנשי קשר"); })
        .finally(() => { if (active) setSenderContactLoading(false); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [senderContactQuery, senderEditTab, isEditingSender]);

  const selectSenderContact = (contact) => {
    if (!contact.email) return;
    const payload = {
      sender_name: `${contact.first_name} ${contact.last_name || ""}`.trim(),
      sender_email: contact.email,
      source_contact_id: contact.id,
    };
    if (contact.company_id) payload.company_id = contact.company_id;
    setSenderSaveError(null);
    setShowAddSenderContact(false);
    updateSender.mutate(payload);
  };

  const openAddSenderContact = (sender = null) => {
    setContactCreationContext(sender ? "email-thread" : "ticket-sender");
    setAddContactDraft(contactDraftForEmailSender(sender, ticket));
    setAddContactError(null);
    setDuplicateContact(null);
    setShowAddSenderContact(true);
  };

  const closeAddSenderContact = () => {
    setShowAddSenderContact(false);
    setAddContactError(null);
    setDuplicateContact(null);
  };
  // No Escape handler previously -- a keyboard user had to find the unlabeled-by-role ✕.
  useEffect(() => {
    if (!showAddSenderContact) return;
    const onKeyDown = (e) => { if (e.key === "Escape") closeAddSenderContact(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showAddSenderContact]);

  const createSenderContact = useMutation({
    mutationFn: (data) => contactsApi.create(data),
    onSuccess: (newContact) => {
      const createdFromThread = contactCreationContext === "email-thread";
      setShowAddSenderContact(false);
      setAddContactError(null);
      setDuplicateContact(null);
      setSenderContactResults((prev) => [newContact, ...prev]);
      qc.invalidateQueries({ queryKey: ["contacts"] });
      if (createdFromThread) {
        addNotification("השולח נוסף לאנשי הקשר בהצלחה");
      }
      flashSavedMsg();
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.contact) {
        if (contactCreationContext === "email-thread") {
          setShowAddSenderContact(false);
          qc.invalidateQueries({ queryKey: ["contacts"] });
          addNotification("השולח כבר שמור באנשי הקשר");
          return;
        }
        setDuplicateContact(detail.contact);
        setAddContactError(detail.message || "איש קשר עם אימייל זה כבר קיים");
      } else {
        setAddContactError(typeof detail === "string" ? detail : "שגיאה ביצירת איש קשר");
      }
    },
  });

  const confirmAddSenderContact = () => {
    const first_name = addContactDraft.first_name.trim();
    const email = addContactDraft.email.trim();
    if (!first_name) { setAddContactError("שם הוא שדה חובה"); return; }
    if (!email) { setAddContactError("אימייל הוא שדה חובה"); return; }
    setAddContactError(null);
    createSenderContact.mutate(contactPayloadFromSenderDraft(addContactDraft, id));
  };

  const applyDuplicateContact = () => {
    if (!duplicateContact) return;
    selectSenderContact(duplicateContact);
  };

  const suggestSolution = useMutation({
    mutationFn: () => aiApi.suggest(id),
    onSuccess: (data) => { setAiSolution(data.solution); setShowAiPanel(true); },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בקבלת הצעת AI", "error"),
  });

  const { data: spFolders = [] } = useQuery({
    queryKey: ["sharepoint-folders"],
    queryFn:  () => sharepointApi.folders(),
    staleTime: 5 * 60_000,
  });

  const spSearch = useMutation({
    mutationFn: ({ query, folderId }) =>
      sharepointApi.search(query, id, folderId || null),
    onSuccess: () => setShowSpPanel(true),
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בחיפוש SharePoint", "error"),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }
  if (!ticket) {
    // only a 404 is "not found": a 500, 403 or a dropped connection is a load failure that can be retried
    const notFound = ticketFailed && [404, 422].includes(ticketError?.response?.status);   // 422: a malformed id in the URL
    return ticketFailed && !notFound ? (
      <div role="alert" className="text-red-500 p-4 space-y-2">
        <p>לא ניתן לטעון את הקריאה</p>
        <button type="button" onClick={() => refetchTicket()} className="text-sm text-brand-600 hover:underline">נסה שוב</button>
      </div>
    ) : <p className="text-red-500 p-4">קריאה לא נמצאה</p>;
  }

  const currentStatus = pendingStatus !== undefined ? pendingStatus : ticket.status;
  const currentTechId = pendingTechId !== undefined ? pendingTechId : (ticket.technician_id ?? "");
  // "Changed" means different from what the server has: clicking a status and clicking back left a pending value equal
  // to the current one, enabled Save, and saving it cleared the customer-reply flag and mailed the technician.
  const { statusDirty, techDirty } = dirtyPendingChanges(ticket, pendingStatus, pendingTechId);
  const hasChanges = statusDirty || techDirty;

  const handleSave = () => {
    const payload = {};
    if (statusDirty || (techDirty && pendingStatus !== undefined)) payload.status = pendingStatus;
    if (techDirty) payload.technician_id = pendingTechId || null;
    updateTicket.mutate(payload);
  };

  const activeAiRun = ticketAgentRuns.find(r => AI_BLOCKING_STATUSES.has(r.status));
  const activeAiIsReview = activeAiRun && (
    activeAiRun.status === "awaiting_technician_review" || activeAiRun.status === "awaiting_approval"
  );
  const AI_INSIGHT_STATUSES = new Set(["awaiting_technician_review", "awaiting_approval", "completed", "approved"]);
  const aiInsightRun = ticketAgentRuns.find(r => AI_INSIGHT_STATUSES.has(r.status) && r.analysis?.likely_issue);
  const aiResolutionSuggestion = [...ticketAgentRuns]
    .filter(r => ["completed", "approved", "resolved"].includes(r.status) && r.analysis?.likely_issue)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0]?.analysis ?? null;
  // Both surfaces below draw a run's own `analysis` prose. On the pilot default that prose is the
  // fallback adapter's scaffolding, not a finding about this ticket, so each says so where it is read.
  const aiInsightDegraded = degradedAnalysisLabel(aiInsightRun?.analysis?.degraded);
  const aiResolutionDegraded = degradedAnalysisLabel(aiResolutionSuggestion?.degraded);

  const handleQuickApproveComment = async (e) => {
    e.stopPropagation();
    // Guards against BOTH flags, not just its own: approve and reject act on the same
    // activeAiRun.id, so a fast approve-then-reject double-action (each button only checking its
    // own pending flag) used to fire both requests at once instead of being mutually exclusive,
    // matching the shared-busy-flag pattern AgentRunPanel/AgentRunDetailPage already use for the
    // same run-level actions.
    if (!activeAiRun || quickApproving || quickRejecting) return;
    setQuickApproving(true);
    try {
      await agentRunsApi.approve(activeAiRun.id);
      qc.invalidateQueries({ queryKey: ["agentRuns", id] });
      qc.invalidateQueries({ queryKey: ["employee-activity", id] });
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
      qc.invalidateQueries({ queryKey: ["agents-dash"] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      addNotification("הערה אושרה ונוספה לקריאה", "success");
    } catch (err) {
      addNotification(err?.response?.data?.detail || "שגיאה באישור", "error");
    } finally {
      setQuickApproving(false);
    }
  };

  const handleQuickReject = async (e) => {
    e.stopPropagation();
    if (!activeAiRun || quickApproving || quickRejecting) return;
    setQuickRejecting(true);
    try {
      await agentRunsApi.reject(activeAiRun.id, "ניתוח לא מדויק");
      qc.invalidateQueries({ queryKey: ["agentRuns", id] });
      qc.invalidateQueries({ queryKey: ["employee-activity", id] });
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
      qc.invalidateQueries({ queryKey: ["agents-dash"] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      addNotification("ניתוח AI נדחה", "info");
    } catch (err) {
      addNotification(err?.response?.data?.detail || "שגיאה בדחייה", "error");
    } finally {
      setQuickRejecting(false);
    }
  };

  const handleStartAiAnalysis = async () => {
    setAiAnalyzing(true);
    try {
      // The ticket's assigned Digital Employee does the ticket's work. With nothing assigned (or an
      // employee the payload marks disabled) no agent_id is sent, so the backend keeps its own
      // default and an unassigned ticket behaves exactly as before.
      await agentRunsApi.start(id, assignedAgentId || undefined);
      qc.invalidateQueries({ queryKey: ["agentRuns", id] });
      qc.invalidateQueries({ queryKey: ["employee-activity", id] });
      setActiveTab("ai-diagnosis");
    } catch (err) {
      addNotification(err?.response?.data?.detail || "שגיאה בהפעלת ניתוח AI", "error");
    } finally {
      setAiAnalyzing(false);
    }
  };

  // On returning to the tab an UNEDITED To/CC follows the ticket (the sender or contact may have been corrected
  // meanwhile), an edited one is left alone. (autoRecipientsRef is declared with the other refs, above the early return.)
  const refreshUneditedRecipients = () => {
    const recipients = emailRecipientDefaults(ticket);
    if (emailTo === autoRecipientsRef.current.to) setEmailTo(recipients.to);
    if (emailCc === autoRecipientsRef.current.cc) setEmailCc(recipients.cc);
    autoRecipientsRef.current = recipients;
  };

  const openEmailForm = () => {
    setEmailBodyError(null);
    const recipients = emailRecipientDefaults(ticket);
    autoRecipientsRef.current = recipients;
    setEmailTo(recipients.to);
    setEmailCc(recipients.cc);
    setEmailSubject(cleanEmailSubject(ticket.title || ""));
    setEmailFiles([]);
    setIncludeEmailThread(false);
    setEmailBody((current) => (hasEmailSignature(current) ? current : `<br>${buildEmailSignature(user, technicians)}`));
    setShowEmail(true);
  };

  const fillEmailWithDraft = (draft) => {
    if (!draft) return;
    const recipients = emailRecipientDefaults(ticket);
    setEmailTo((cur) => cur || recipients.to);
    setEmailCc((cur) => cur || recipients.cc);
    setEmailSubject((cur) => cur || cleanEmailSubject(ticket.title || ""));
    setEmailFiles([]);
    setIncludeEmailThread(false);
    const htmlDraft = plainTextToEditorHtml(draft);
    const sig = buildEmailSignature(user, technicians);
    setEmailBody(`${htmlDraft}<br><br>${sig}`);
    setShowEmail(true);
    setActiveTab("sendEmail");
  };

  const loadContactsForPicker = async () => {
    setContactsLoading(true);
    try {
      const result = await contactsApi.list();
      setContacts(result || []);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const openContactPicker = () => {
    setContactSearch("");
    setShowContactPicker(true);
    loadContactsForPicker();
  };

  const filteredContacts = contactSearch.trim()
    ? contacts.filter((contact) => contactSearchHaystack(contact).includes(contactSearch.trim().toLowerCase()))
    : contacts;

  const addContactEmailToRecipient = (contact) => {
    if (!contact.email) return;
    setEmailTo((current) => appendEmailRecipient(current, contact.email));
    setShowContactPicker(false);
  };

  const handleSendEmail = (e) => {
    e.preventDefault();
    if (canSendCustomerEmail({ to: emailTo, subject: emailSubject, body: emailBody })) {
      sendEmail.mutate({
        to: emailTo, cc: emailCc, subject: emailSubject.trim(), body: emailBody,
        includeEmailThread, files: emailFiles,
      });
    }
  };

  const sortedEmailThread = [...emailThread].sort(
    (a, b) =>
      timeValue(a.received_at, a.sent_at, a.created_at) -
      timeValue(b.received_at, b.sent_at, b.created_at)
  );
  const sortedComments = [...comments].sort(
    (a, b) => timeValue(a.created_at) - timeValue(b.created_at)
  );
  const sortedHistory = [...history].sort(
    (a, b) => timeValue(a.created_at) - timeValue(b.created_at)
  );
  const ticketAttachmentCount = Math.max(attachmentCount(ticket), ticketAttachments.length);
  const hasTicketAttachments = ticketHasAttachments(ticket) || ticketAttachments.length > 0;
  const detailTabs = [
    { id: "email-thread", label: "שרשור מיילים" },
    { id: "comments", label: "הערות" },
    { id: "attachments", label: ticketAttachmentCount > 0 ? `קבצים (${ticketAttachmentCount})` : "קבצים" },
    { id: "history", label: "יומן פעילויות כרטיס" },
    { id: "sendEmail", label: "שליחת מייל" },
    ...(canAgent("agents_view") ? [{
      id: "workbench",
      label: "סביבת עבודה",
      badge: ticketAgentRuns.filter(r =>
        r.status === "awaiting_technician_review" || r.status === "awaiting_approval"
      ).length || null,
    }] : []),
    ...(canAgent("agents_view") ? [{
      id: "ai-diagnosis",
      label: "אבחון AI",
      badge: ticketAgentRuns.filter(r =>
        r.status === "awaiting_technician_review" || r.status === "awaiting_approval"
      ).length || null,
    }] : []),
    ...(canAgent("agents_view") ? [{ id: "ai-chat", label: "שיחה עם AI" }] : []),
  ];

  return (
    <>
    <div className="w-full max-w-[1320px] 2xl:max-w-[1500px] mx-auto space-y-3 2xl:space-y-4 px-3 xl:px-4 2xl:px-5" dir="rtl">

      {/* Linked project banner */}
      {ticket.linked_project_id && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl px-3 xl:px-4 py-2.5 xl:py-3 flex items-center gap-2 text-sm">
          <span className="text-purple-700 font-medium">קריאה זו מנוהלת כמשימה בפרויקט:</span>
          <Link
            to={`/projects/${ticket.linked_project_id}`}
            className="text-purple-600 font-semibold hover:underline"
          >
            {linkedProject?.title ?? "..."}
          </Link>
        </div>
      )}

      {/* Linked maintenance item banner */}
      {ticket.linked_maintenance_id && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-3 xl:px-4 py-2.5 xl:py-3 flex items-center gap-2 text-sm">
          <span className="text-green-700 font-medium">קריאה זו מנוהלת כפריט תחזוקה:</span>
          <Link
            to={`/maintenance/${ticket.linked_maintenance_id}`}
            className="text-green-600 font-semibold hover:underline"
          >
            {linkedMaintItem?.title ?? "..."}
          </Link>
        </div>
      )}

      {/* AI insight banner — shown when a run has a likely_issue */}
      {canAgent("agents_view") && aiInsightRun && (
        <div
          className={`rounded-xl border px-3 xl:px-4 py-2.5 flex items-start gap-3 text-sm cursor-pointer hover:opacity-90 ${
            activeAiIsReview
              ? "bg-orange-50 border-orange-200"
              : "bg-violet-50 border-violet-200"
          }`}
          onClick={() => setActiveTab("ai-diagnosis")}
          title="פתח אבחון AI"
        >
          <span className="text-lg mt-0.5">🤖</span>
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold mb-0.5 ${activeAiIsReview ? "text-orange-700" : "text-violet-700"}`}>
              {activeAiIsReview ? "AI — ממתין לסקירה" : "AI — ניתוח הושלם"}
              {aiInsightRun.analysis?.confidence != null && (
                <span className="ml-2 font-normal opacity-75">({aiInsightRun.analysis.confidence}% ביטחון)</span>
              )}
            </p>
            {aiInsightDegraded && (
              <span
                data-testid="ticket-ai-insight-degraded"
                className="inline-block text-[10px] font-medium leading-none bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5 mb-1"
              >
                {aiInsightDegraded}
              </span>
            )}
            <p className="text-gray-700 text-xs leading-snug line-clamp-2">
              {aiInsightRun.analysis.likely_issue}
            </p>
            {aiInsightRun.analysis?.recommended_next_step && (
              <p className="text-gray-500 text-xs leading-snug line-clamp-1 mt-0.5">
                ← {aiInsightRun.analysis.recommended_next_step}
              </p>
            )}
            {activeAiIsReview && aiInsightRun.analysis?.proposed_action?.action_type && (
              <p className="text-orange-600 text-xs font-medium mt-0.5">
                ⚡ {actionTypeLabel(aiInsightRun.analysis.proposed_action.action_type)}
              </p>
            )}
            {Array.isArray(aiInsightRun.analysis?.missing_information) && aiInsightRun.analysis.missing_information.length > 0 && (
              <p className="text-amber-600 text-xs mt-0.5 leading-snug line-clamp-2" title={aiInsightRun.analysis.missing_information.join(" · ")}>
                ❓ {aiInsightRun.analysis.missing_information.slice(0, 2).join(" · ")}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {activeAiIsReview && activeAiRun?.analysis?.proposed_action?.action_type === "add_comment" && canAgent("agents_approve") && (
              <button
                type="button"
                onClick={handleQuickApproveComment}
                disabled={quickApproving || quickRejecting}
                className="text-xs font-medium bg-orange-100 hover:bg-orange-200 text-orange-700 border border-orange-300 px-2 py-0.5 rounded transition-colors disabled:opacity-50"
              >
                {quickApproving ? "..." : "✓ אשר הערה"}
              </button>
            )}
            {activeAiIsReview && activeAiRun?.analysis?.customer_reply_draft && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); fillEmailWithDraft(activeAiRun.analysis.customer_reply_draft); }}
                className="text-xs font-medium bg-orange-100 hover:bg-orange-200 text-orange-700 border border-orange-300 px-2 py-0.5 rounded transition-colors"
                title="מלא טופס מייל עם טיוטת AI לסקירה לפני שליחה"
              >
                ✉ ערוך ושלח
              </button>
            )}
            {activeAiIsReview && canAgent("agents_approve") && (
              <button
                type="button"
                onClick={handleQuickReject}
                disabled={quickApproving || quickRejecting}
                className="text-xs font-medium bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 px-2 py-0.5 rounded transition-colors disabled:opacity-50"
                title="דחה ניתוח AI (סיבה: ניתוח לא מדויק) — לסיבה מפורטת עבור ללשונית AI"
              >
                {quickRejecting ? "..." : "✗ דחה"}
              </button>
            )}
            <span className={`text-xs font-medium whitespace-nowrap ${activeAiIsReview ? "text-orange-600" : "text-violet-600"}`}>
              ← פרטים
            </span>
          </div>
        </div>
      )}

      <TicketEmployeeActivityPanel ticketId={ticket.id} canView={canAgent("agents_view")} />

      <TicketEmployeeAssignmentPanel
        ticketId={ticket.id}
        companyId={ticket.company_id}
        canView={canAgent("agents_view")}
        canAssign={canAgent("agents_run")}
      />

      {/* Header card */}
      <div className="bg-white rounded-xl shadow-sm border p-3 xl:p-4 2xl:p-5 space-y-3 2xl:space-y-4">
        <div className="flex items-start justify-between gap-2 xl:gap-3 flex-wrap-reverse">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => navigate(backTo)} className="text-brand-600 text-sm hover:underline flex items-center gap-1">
              ← חזרה לרשימת הקריאות
            </button>
            <div className="flex items-center gap-1 border-r pr-2">
              <button
                type="button"
                onClick={() => goToAdjacentTicket(prevTicketId)}
                disabled={!prevTicketId}
                title="הקריאה הקודמת"
                className="text-sm px-2 py-1 rounded-lg border flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                → הקריאה הקודמת
              </button>
              <button
                type="button"
                onClick={() => goToAdjacentTicket(nextTicketId)}
                disabled={!nextTicketId}
                title="הקריאה הבאה"
                className="text-sm px-2 py-1 rounded-lg border flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                הקריאה הבאה ←
              </button>
            </div>
            {canAgent("agents_run") && (
              <button
                type="button"
                onClick={activeAiRun ? () => setActiveTab("ai-diagnosis") : handleStartAiAnalysis}
                disabled={aiAnalyzing}
                title={activeAiIsReview ? "ניתוח AI ממתין לסקירה — לחץ לפתיחת הטאב" : activeAiRun ? "ניתוח AI בתהליך — לחץ לצפייה" : "נתח את הקריאה עם AI"}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeAiIsReview
                    ? "bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100"
                    : activeAiRun
                      ? "bg-violet-50 border border-violet-200 text-violet-700 hover:bg-violet-100"
                      : "bg-violet-600 hover:bg-violet-700 text-white"
                } disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {aiAnalyzing ? "מנתח..." : activeAiIsReview ? "🤖 ממתין לסקירה" : activeAiRun ? "🤖 AI בתהליך" : "🤖 נתח עם AI"}
              </button>
            )}
          </div>
          <div className="flex-1 min-w-[18rem]">
            <div className="flex items-center justify-end gap-2 mb-1 flex-wrap text-sm 2xl:text-base">
              <span className="text-xs font-mono text-gray-400">#{ticket.ticket_number}</span>
              {hasTicketAttachments && (
                <span
                  title={`${ticketAttachmentCount} קבצים מצורפים`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200"
                >
                  📎
                  {ticketAttachmentCount}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[ticket.status]}`}>
                {ticketStatusLabel(ticket.status)}
              </span>
              <span className="text-xs text-gray-500">
                עדיפות: <strong>{PRIORITY_LABELS[ticket.priority]}</strong>
              </span>
              {ticket.is_sla_breach && (
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-300">
                  ⚠ חריגת SLA
                </span>
              )}
              {ticket.queue_type && ticket.queue_type !== "ticket" && (
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full border ${
                  ticket.queue_type === "project" ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-green-50 text-green-700 border-green-200"
                }`}>
                  {ticket.queue_type === "project" ? "📁 בתור פרויקטים" : "🔧 בתור תחזוקה"}
                </span>
              )}
              {ticket.ai_category && (
                <span
                  data-testid="ticket-ai-category"
                  className="inline-block text-xs font-medium px-2.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200"
                >
                  {ticketCategoryLabel(ticket.ai_category)}
                </span>
              )}
            </div>
            {isEditingTitle ? (
              <div className="space-y-2 mt-1">
                <input
                  type="text"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  className="w-full text-xl font-bold text-gray-800 border border-brand-400 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && draftTitle.trim()) updateTitle.mutate(draftTitle.trim());
                    if (e.key === "Escape") setIsEditingTitle(false);
                  }}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { if (draftTitle.trim()) updateTitle.mutate(draftTitle.trim()); }}
                    disabled={!draftTitle.trim() || updateTitle.isPending}
                    className="px-3 py-1 text-xs bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                  >
                    {updateTitle.isPending ? "שומר..." : "שמור"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditingTitle(false)}
                    className="px-3 py-1 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                  >
                    ביטול
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-2 mt-1">
                <h1 className="text-xl font-bold text-gray-800 text-right">{ticket.title}</h1>
                <button
                  type="button"
                  onClick={() => { setDraftTitle(ticket.title); setIsEditingTitle(true); }}
                  className="inline-flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                  title="ערוך כותרת"
                  aria-label="ערוך כותרת"
                >
                  ✏️
                </button>
              </div>
            )}
          </div>
        </div>

        {ticket.split_from_id && ticket.split_from_number && (
          <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2.5 text-sm text-indigo-800">
            <span className="shrink-0">🔀</span>
            <span>
              קריאה זו פוצלה מ{" "}
              <Link to={`/tickets/${ticket.split_from_id}`} className="font-semibold hover:underline">
                קריאה #{ticket.split_from_number}
              </Link>
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3 text-sm border-y py-3">
          <div className="space-y-0.5">
            <p className="text-xs text-gray-400">שולח</p>
            {!isEditingSender ? (
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-gray-700 break-words">{ticket.sender_name}</p>
                  {ticket.contact_role && (
                    <p className="text-[11px] text-indigo-600 font-medium">{ticket.contact_role}</p>
                  )}
                  <p className="text-xs text-gray-400 truncate">{ticket.sender_email}</p>
                  {ticket.sender_email && (
                    <Link
                      to={`/tickets?sender_email=${encodeURIComponent(ticket.sender_email)}&status=all`}
                      className="text-[10px] text-brand-500 hover:underline"
                      title="כל הקריאות מהשולח הזה"
                    >
                      היסטוריה →
                    </Link>
                  )}
                </div>
                <button
                  type="button"
                  onClick={startEditSender}
                  className="inline-flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 mt-0.5"
                  title="ערוך שולח"
                  aria-label="ערוך שולח"
                >
                  ✏️
                </button>
              </div>
            ) : (
              <div className="space-y-2 border rounded-lg p-2 bg-gray-50">
                <div className="flex gap-1 border-b pb-1.5">
                  <button
                    type="button"
                    onClick={() => setSenderEditTab("manual")}
                    className={`px-2 py-1 rounded text-xs font-medium ${senderEditTab === "manual" ? "bg-brand-600 text-white" : "text-gray-500 hover:bg-gray-100"}`}
                  >
                    עריכה ידנית
                  </button>
                  <button
                    type="button"
                    onClick={() => setSenderEditTab("contacts")}
                    className={`px-2 py-1 rounded text-xs font-medium ${senderEditTab === "contacts" ? "bg-brand-600 text-white" : "text-gray-500 hover:bg-gray-100"}`}
                  >
                    אנשי קשר
                  </button>
                </div>

                {senderEditTab === "manual" ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={draftSenderName}
                      onChange={(e) => setDraftSenderName(e.target.value)}
                      placeholder="שם שולח"
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    />
                    <input
                      type="email"
                      value={draftSenderEmail}
                      onChange={(e) => setDraftSenderEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                      dir="ltr"
                    />
                    {senderSaveError && <p className="text-xs text-red-500">{senderSaveError}</p>}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSaveSender}
                        disabled={updateSender.isPending}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                      >
                        {updateSender.isPending ? "שומר..." : "שמור"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditSender}
                        className="px-3 py-1.5 rounded-lg text-xs border border-gray-300 text-gray-600 hover:bg-gray-50"
                      >
                        ביטול
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      autoFocus
                      value={senderContactQuery}
                      onChange={(e) => setSenderContactQuery(e.target.value)}
                      placeholder="חיפוש לפי שם, אימייל או חברה"
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    />
                    {senderContactLoading && (
                      <div className="flex justify-center py-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600" />
                      </div>
                    )}
                    {!senderContactLoading && senderContactError && (
                      <p className="text-xs text-red-500">{senderContactError}</p>
                    )}
                    {!senderContactLoading && !senderContactError && (
                      senderContactResults.length === 0 ? (
                        <div className="text-center py-2 space-y-2">
                          <p className="text-xs text-gray-400">לא נמצאו אנשי קשר</p>
                          <button
                            type="button"
                            onClick={() => openAddSenderContact()}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700"
                          >
                            הוסף כאיש קשר חדש
                          </button>
                        </div>
                      ) : (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {senderContactResults.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              disabled={!c.email}
                              onClick={() => selectSenderContact(c)}
                              title={!c.email ? "לאיש קשר זה אין אימייל" : ""}
                              className="w-full text-right border rounded-lg px-2 py-1.5 bg-white hover:bg-brand-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <p className="font-medium text-gray-700 text-xs">{c.first_name} {c.last_name || ""}</p>
                              <p className="text-xs text-gray-400 truncate" dir="ltr">{c.email || "ללא כתובת אימייל"}</p>
                              <p className="text-xs text-gray-400 truncate">
                                {c.company_name || "—"}{c.phone ? ` · ${c.phone}` : ""}
                              </p>
                            </button>
                          ))}
                        </div>
                      )
                    )}
                    <div className="flex justify-between items-center pt-1 border-t">
                      <button
                        type="button"
                        onClick={() => openAddSenderContact()}
                        className="text-xs text-brand-600 hover:underline"
                      >
                        + הוסף לאנשי קשר
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditSender}
                        className="px-3 py-1.5 rounded-lg text-xs border border-gray-300 text-gray-600 hover:bg-gray-50"
                      >
                        ביטול
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-gray-400">חברה</p>
            <div className="relative">
              <div className="flex items-center gap-1.5">
                <select
                  value={localCompany !== undefined ? (localCompany?.id ?? "") : (ticket.company?.id ?? "")}
                  onChange={(e) => {
                    const companyId = e.target.value || null;
                    const company = companyId ? (companies.find((c) => c.id === companyId) ?? null) : null;
                    setLocalCompany(company);
                    setCompanySaveError(null);
                    updateCompany.mutate(companyId);
                  }}
                  disabled={updateCompany.isPending}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white text-gray-700 disabled:opacity-50"
                >
                  <option value="">ללא חברה</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              {showAddCompanyForm && (
                <div
                  className="absolute top-full mt-1 right-0 w-64 bg-white border rounded-lg shadow-lg p-3 z-30 space-y-2"
                  dir="rtl"
                >
                  <p className="text-xs font-medium text-gray-600">חברה חדשה</p>
                  <input
                    type="text"
                    placeholder="שם חברה"
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
                    autoFocus
                  />
                  <input
                    type="text"
                    placeholder="דומיין (לדוגמה: company.co.il)"
                    value={newCompanyDomain}
                    onChange={(e) => setNewCompanyDomain(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddCompany()}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
                  />
                  {newCompanyError && (
                    <p className="text-xs text-red-500">{newCompanyError}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddCompany}
                      disabled={createCompany.isPending}
                      className="flex-1 bg-brand-600 text-white text-xs py-1.5 rounded hover:bg-brand-700 disabled:opacity-50"
                    >
                      {createCompany.isPending ? "שומר..." : "הוסף"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddCompanyForm(false);
                        setNewCompanyName("");
                        setNewCompanyDomain("");
                        setNewCompanyError(null);
                      }}
                      className="flex-1 border text-xs py-1.5 rounded hover:bg-gray-50 text-gray-600"
                    >
                      ביטול
                    </button>
                  </div>
                </div>
              )}
            </div>
            {updateCompany.isPending && (
              <p className="text-xs text-gray-400 mt-0.5">שומר...</p>
            )}
            {companySaveError && (
              <p className="text-xs text-red-500 mt-0.5">{companySaveError}</p>
            )}
            {/* The profile API is admin/owner-only: other roles used to open an empty editable form that 403'd on save. */}
            {["admin", "owner"].includes(user?.role) && (localCompany !== undefined ? localCompany?.id : ticket.company?.id) && (
              <Link
                to={`/companies/${localCompany !== undefined ? localCompany?.id : ticket.company?.id}/profile`}
                className="text-xs text-brand-600 hover:underline mt-0.5 inline-block"
              >
                🖥 סביבת IT →
              </Link>
            )}
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-gray-400">תאריך פתיחה</p>
            <p className="font-medium text-gray-700 whitespace-nowrap min-w-[9rem]">
              {(() => {
                const d = new Date(asUtc(ticket.opened_at));
                const p = (n) => String(n).padStart(2, "0");
                return `${p(d.getDate())}.${p(d.getMonth()+1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
              })()}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-gray-400">עדכון אחרון</p>
            <p className="font-medium text-gray-700 whitespace-nowrap min-w-[9rem]">
              {(() => {
                const d = new Date(asUtc(ticket.updated_at));
                const p = (n) => String(n).padStart(2, "0");
                return `${p(d.getDate())}.${p(d.getMonth()+1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
              })()}
            </p>
          </div>
          {ticket.status === "closed" ? (
            ticket.resolution_note && (
              <div className="space-y-0.5">
                <p className="text-xs text-gray-400">פתרון</p>
                <p className="text-xs text-gray-600 bg-green-50 border border-green-100 rounded px-2 py-1.5 leading-relaxed">
                  {ticket.resolution_note}
                </p>
              </div>
            )
          ) : (
            <div className="space-y-0.5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">פתרון (טיוטה לניתוח AI)</p>
                {draftResolutionNote !== (ticket.resolution_note || "") && (
                  <button
                    type="button"
                    onClick={() => updateTicket.mutate({ resolution_note: draftResolutionNote.trim() })}
                    disabled={updateTicket.isPending}
                    className="text-[10px] bg-brand-600 hover:bg-brand-700 text-white rounded px-1.5 py-0.5 disabled:opacity-50"
                  >
                    שמור
                  </button>
                )}
              </div>
              <textarea
                value={draftResolutionNote}
                onChange={(e) => setDraftResolutionNote(e.target.value)}
                placeholder="תאר את הפתרון הצפוי — ישמש AI בניתוח הבא"
                rows={2}
                className="w-full text-xs text-gray-700 border border-gray-200 rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            </div>
          )}
          <div className="space-y-0.5">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">טכנאי משובץ</p>
              <div className="flex items-center gap-1">
                {myTechnicianId && currentTechId !== myTechnicianId && (
                  <button
                    type="button"
                    onClick={() => setPendingTechId(myTechnicianId)}
                    className="text-[10px] bg-brand-50 hover:bg-brand-100 text-brand-700 border border-brand-200 px-1.5 py-0.5 rounded transition-colors"
                    title="שבץ את הקריאה אליי"
                  >
                    👋 שבץ אליי
                  </button>
                )}
                {assigneeSuggestion && !ticket.technician_id && (
                  <button
                    type="button"
                    onClick={() => setPendingTechId(assigneeSuggestion.id)}
                    className="text-[10px] bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 px-1.5 py-0.5 rounded transition-colors"
                    title={`${assigneeSuggestion.full_name} — ${assigneeSuggestion.resolved} קריאות דומות ב-90 יום האחרונים`}
                  >
                    🤖 {assigneeSuggestion.full_name}
                  </button>
                )}
              </div>
            </div>
            <select
              value={currentTechId}
              onChange={(e) => setPendingTechId(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">ללא שיבוץ</option>
              {technicians.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.first_name} {t.last_name}
                </option>
              ))}
            </select>
            {ticket.technician && !hasChanges && (
              <p className="text-xs text-gray-400 truncate">
                {ticket.technician.email} {ticket.technician.phone ? `· ${ticket.technician.phone}` : ""}
              </p>
            )}
          </div>
          <div className="space-y-0.5">
            <label className="block text-xs text-gray-400">מועד יעד לביצוע</label>
            <input aria-label="מועד יעד לביצוע" type="date" value={ticket.due_date?.slice(0, 10) || ""} onChange={(e) => updateTicket.mutate({ due_date: e.target.value || null })} disabled={updateTicket.isPending} className="w-full border rounded-lg px-3 py-2 text-sm" />
            {isTicketOverdue(ticket) && <p className="text-xs font-bold text-red-700">😞 הקריאה באיחור</p>}
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-gray-400">התראה לטכנאי</p>
            <button
              onClick={() => notifyTechnician.mutate()}
              disabled={!ticket.technician || notifyTechnician.isPending}
              className="inline-flex items-center justify-center gap-1.5 border border-gray-300 hover:border-gray-400 px-3 py-2 rounded-lg text-sm text-gray-600 hover:text-gray-800 transition-colors bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              title="שלח מייל לטכנאי על השיבוץ"
            >
              {notifyTechnician.isPending ? "שולח..." : "📩 הודע לטכנאי"}
            </button>
          </div>
        </div>

        <TicketDescriptionPanel
          ticket={ticket}
          attachments={ticketAttachments}
          isEditing={isEditingBody}
          draftBody={draftBody}
          onDraftBodyChange={setDraftBody}
          onStartEdit={startEditBody}
          onSave={saveBody}
          onCancel={cancelEditBody}
          isSaving={updateBody.isPending}
        />

        <TicketSummaryPanel summary={ticket.ai_summary} />

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-2">שינוי סטטוס</label>
            <div className="flex gap-2 flex-wrap">
              {TICKET_STATUS_EDIT_ORDER.map((val) => (
                <button
                  key={val}
                  onClick={() => {
                    if (val === "closed" && ticket.status !== "closed") {
                      setCloseResolutionNote(draftResolutionNote.trim());
                      updateTicket.reset();
                      setShowCloseModal(true);
                    } else {
                      setPendingStatus(val === ticket.status && pendingStatus === undefined ? undefined : val);
                    }
                  }}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    currentStatus === val
                      ? STATUS_COLORS[val] + " ring-2 ring-offset-1 ring-current"
                      : "border-gray-300 text-gray-500 hover:border-gray-400 bg-white"
                  }`}
                >
                  {ticketStatusLabel(val)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowMoveQueue(true)}
              className="flex items-center justify-center gap-1.5 text-xs border border-gray-300 hover:border-gray-400 px-3 py-2 rounded-lg text-gray-600 hover:text-gray-800 transition-colors bg-white"
              title="העבר קריאה לתור אחר"
            >
              ↔ העבר לתור
            </button>
            <button
              onClick={() => {
                setActiveTab("comments");
                const section = document.getElementById("ticket-comments-section");
                const input = document.getElementById("ticket-comment-input");
                setTimeout(() => {
                  section?.scrollIntoView({ behavior: "smooth", block: "start" });
                  input?.focus();
                }, 0);
              }}
              className="flex items-center justify-center gap-1.5 text-xs border border-gray-300 hover:border-gray-400 px-3 py-2 rounded-lg text-gray-600 hover:text-gray-800 transition-colors bg-white"
              title="עבור לאזור הערות"
            >
              + הוסף הערה
            </button>
            <button
              onClick={() => confirmTicketDeletion(ticket, window.confirm, () => deleteTicket.mutate())}
              disabled={deleteTicket.isPending}
              className="flex items-center justify-center gap-1.5 text-xs text-red-500 hover:text-red-600 border border-red-200 hover:border-red-400 px-3 py-2 rounded-lg transition-colors bg-white disabled:opacity-40"
            >
              {deleteTicket.isPending ? "מוחק..." : "מחק קריאה"}
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || updateTicket.isPending}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                hasChanges
                  ? "bg-brand-600 hover:bg-brand-700 text-white"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              } disabled:opacity-60`}
            >
              {updateTicket.isPending ? "שומר..." : "שמור שינויים"}
            </button>
            {hasChanges && (
              <button
                onClick={() => { setPendingStatus(undefined); setPendingTechId(undefined); }}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                בטל שינויים
              </button>
            )}
            {savedMsg && (
              <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                ✓ השינויים נשמרו בהצלחה
              </span>
            )}
          </div>

          <div className="border-t pt-3 xl:pt-4 space-y-3 xl:space-y-4">
            <div className="flex flex-wrap gap-2">
              {detailTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      // Only the FIRST time: the form state persists while another tab is shown, and re-running the
                      // reset on every return wiped the CC, subject and attachments (and the body if the signature
                      // had been removed).
                      if (tab.id === "sendEmail" && activeTab !== "sendEmail") {
                        if (!showEmail) openEmailForm();
                        else refreshUneditedRecipients();
                      }
                      setActiveTab(tab.id);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-brand-600 text-white shadow-sm"
                        : "bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {tab.label}
                    {tab.badge > 0 && (
                      <span className="mr-1 bg-orange-500 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {activeTab === "sendEmail" && (
              <div className="rounded-lg border bg-gray-50/40 p-3 xl:p-4 space-y-4">
                <h2 className="font-semibold text-gray-700">שליחת מייל ללקוח</h2>
                {ticket?.sender_email === "system@prodigy.internal" ? (
                  <p className="text-sm text-gray-500">קריאה זו נוצרה על ידי בדיקה מתוזמנת — אין לקוח אמיתי לשליחת מייל.</p>
                ) : !showEmail && (
                  <button
                    type="button"
                    onClick={openEmailForm}
                    className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    שלח מייל ללקוח
                  </button>
                )}
                {showEmail && ticket?.sender_email !== "system@prodigy.internal" && (
                  <form
                    onSubmit={handleSendEmail}
                    onKeyDown={blockEnterSubmit}
                    className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">אל (To)</label>
                      <div className="flex gap-2">
                        <input
                          required
                          type="text"
                          inputMode="email"
                          value={emailTo}
                          onChange={(e) => setEmailTo(e.target.value)}
                          className="flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm bg-white"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openContactPicker();
                          }}
                          className="shrink-0 border rounded-lg px-3 py-2 text-sm text-gray-600 bg-white hover:bg-gray-50 transition-colors whitespace-nowrap"
                        >
                          + הוסף איש קשר
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">עותק (CC) — אופציונלי</label>
                      <div className="flex flex-wrap gap-2">
                        <input
                          type="text"
                          value={emailCc}
                          onChange={(e) => setEmailCc(e.target.value)}
                          placeholder="email1@example.com, email2@example.com"
                          className="min-w-64 flex-1 border rounded-lg px-3 py-2 text-sm bg-white"
                          dir="ltr"
                        />
                        <select
                          value=""
                          onChange={(e) => {
                            if (!e.target.value) return;
                            setEmailCc((current) => appendEmailRecipient(current, e.target.value));
                          }}
                          className="border rounded-lg px-2 py-2 text-sm text-gray-600 bg-white"
                        >
                          <option value="">+ הוסף טכנאי</option>
                          {technicians.map((t) => (
                            <option key={t.id} value={t.email}>
                              {t.first_name} {t.last_name}
                            </option>
                          ))}
                        </select>
                        <ContactCcPicker
                          onSelect={(email) => setEmailCc((current) => appendEmailRecipient(current, email))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">נושא</label>
                      <input
                        required
                        maxLength={255}
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(stripTicketSubjectPrefix(e.target.value))}
                        dir={hasHebrew(emailSubject) ? "rtl" : "ltr"}
                        className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${hasHebrew(emailSubject) ? "text-right" : "text-left"}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">גוף המייל</label>
                      <RichTextEditor
                        value={emailBody}
                        onChange={setEmailBody}
                        placeholder="כתוב את תוכן המייל כאן... (ניתן להדביק תמונה)"
                        minRows={4}
                        onError={setEmailBodyError}
                      />
                      {emailBodyError && <p className="text-xs text-red-500 mt-1">{emailBodyError}</p>}
                    </div>
                    <EmailThreadCheckbox checked={includeEmailThread} onChange={setIncludeEmailThread} />
                    <FileAttachmentPicker
                      inputId="email-attachments-input"
                      files={emailFiles}
                      onAdd={(files) => setEmailFiles((prev) => [...prev, ...files])}
                      onRemove={(index) => setEmailFiles((prev) => prev.filter((_, i) => i !== index))}
                    />
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={sendEmail.isPending || !canSendCustomerEmail({ to: emailTo, subject: emailSubject, body: emailBody })}
                        className="bg-brand-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                      >
                        {sendEmail.isPending ? "שולח..." : "שלח מייל"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowEmail(false);
                          setEmailTo("");
                          setEmailCc("");
                          setEmailSubject("");
                          setEmailBody("");
                          setEmailFiles([]);
                          setIncludeEmailThread(false);
                          setEmailBodyError(null);
                        }}
                        className="border px-5 py-2 rounded-lg text-sm hover:bg-gray-50 bg-white"
                      >
                        ביטול
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {activeTab === "email-thread" && (
              <div className="rounded-lg border bg-gray-50/40 p-3 xl:p-4 space-y-4">
                <h2 className="font-semibold text-gray-700">התכתבות ({emailThread.length})</h2>
                {emailThreadFailed && emailThread.length === 0 ? (
                  // a failed request is not an empty conversation: the technician would reply blind
                  <LoadFailed message="לא ניתן לטעון את ההתכתבות" onRetry={refetchEmailThread} />
                ) : emailThread.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">אין הודעות בשרשור</p>
                ) : (
                  <div className="space-y-3">
                    {sortedEmailThread.map((msg) => {
                      const isInbound = msg.direction === "inbound";
                      const isExpanded = expandedEmails.has(msg.id);
                      return (
                        <div key={msg.id} className="border rounded-lg overflow-hidden bg-white">
                          <div className={`w-full text-right px-4 py-3 ${isInbound ? "bg-blue-50" : "bg-emerald-50"}`}>
                            <div className="flex items-start gap-3">
                              <span className={`shrink-0 mt-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${isInbound ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                                {isInbound ? "נכנס" : "יוצא"}
                              </span>
                              <div className="flex-1 min-w-0 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-gray-800 truncate">{msg.from_name || msg.from_email}</span>
                                  {msg.from_email && (
                                    <span className="text-xs text-gray-400 truncate hidden sm:block">&lt;{msg.from_email}&gt;</span>
                                  )}
                                  {isInbound && (
                                    <EmailSenderContactAction
                                      message={msg}
                                      contacts={savedContacts}
                                      onAdd={openAddSenderContact}
                                      isLoading={savedContactsLoading}
                                    />
                                  )}
                                  <span className="text-xs text-gray-400 whitespace-nowrap mr-auto">
                                    {new Date(asUtc(msg.received_at)).toLocaleString("he-IL")}
                                  </span>
                                </div>
                                <button type="button" onClick={() => toggleEmail(msg.id)} className="block w-full text-right hover:opacity-80">
                                  <div className="text-sm text-gray-700 truncate">
                                    {msg.subject || "(ללא נושא)"}
                                  </div>
                                  <div className="text-xs text-gray-500 line-clamp-2">
                                    {compactText(msg.body_text || msg.body_preview || "") || "ללא תקציר"}
                                  </div>
                                </button>
                              </div>
                              {msg.id && msg.id !== id && (
                                <button
                                  type="button"
                                  onClick={() => openSplitEmailModal(msg)}
                                  className="shrink-0 text-xs text-gray-500 hover:text-brand-600 border border-gray-200 hover:border-brand-300 rounded-lg px-2 py-1 whitespace-nowrap"
                                  title="פצל לקריאה חדשה"
                                >
                                  🔀 פצל לקריאה חדשה
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => toggleEmail(msg.id)}
                                className="shrink-0 text-gray-400 text-sm hover:text-gray-600"
                                aria-label={isExpanded ? "כווץ הודעה" : "הרחב הודעה"}
                              >
                                {isExpanded ? "▲" : "▼"}
                              </button>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="border-t bg-white">
                              {(msg.to?.length > 0 || msg.cc?.length > 0 || msg.bcc?.length > 0) && (
                                <div className="px-4 py-2 border-b text-xs text-gray-500 bg-gray-50 space-y-0.5">
                                  {msg.to?.length > 0 && (
                                    <div>
                                      אל:{" "}
                                      <span className="font-medium text-gray-700" dir="auto">
                                        {msg.to.map(formatRecipient).join(", ")}
                                      </span>
                                    </div>
                                  )}
                                  {msg.cc?.length > 0 && (
                                    <div>
                                      עותק:{" "}
                                      <span className="font-medium text-gray-700" dir="auto">
                                        {msg.cc.map(formatRecipient).join(", ")}
                                      </span>
                                    </div>
                                  )}
                                  {msg.bcc?.length > 0 && (
                                    <div>
                                      עותק מוסתר:{" "}
                                      <span className="font-medium text-gray-700" dir="auto">
                                        {msg.bcc.map(formatRecipient).join(", ")}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                              {msg.subject && (
                                <div className="px-4 py-2 border-b text-xs text-gray-500 bg-gray-50">
                                  נושא: <span className="font-medium text-gray-700">{msg.subject}</span>
                                </div>
                              )}
                              {hasMeaningfulEmailHtml(msg.body_html) ? (
                                <div className="max-w-full overflow-x-hidden">
                                  <SecureEmailHtml
                                    html={msg.body_html}
                                    attachments={msg.attachments}
                                    fallbackText={msg.body_text}
                                    title={`הודעה מ-${msg.from_name}`}
                                  />
                                </div>
                              ) : msg.body_text?.trim() ? (
                                <div className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-[350px] overflow-y-auto">
                                  {msg.body_text}
                                </div>
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-[350px] overflow-y-auto">
                                  <span className="italic text-gray-400">(ללא תוכן)</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === "attachments" && (
              <div className="rounded-lg border bg-gray-50/40 p-3 xl:p-4 space-y-4">
                <h2 className="font-semibold text-gray-700">קבצים מצורפים ({ticketAttachments.length})</h2>
                {isLoadingAttachments ? (
                  <p className="text-gray-400 text-sm italic">טוען קבצים...</p>
                ) : attachmentsFailed && ticketAttachments.length === 0 ? (
                  // a failed request is not "no attachments": the files exist
                  <p role="alert" className="text-sm text-red-600">לא ניתן לטעון את רשימת הקבצים</p>
                ) : ticketAttachments.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">אין קבצים מצורפים</p>
                ) : (
                  <div className="space-y-2">
                    {ticketAttachments.map((att) => (
                      <button
                        key={att.id}
                        onClick={() => attachmentsApi.download(att.id, att.filename).catch((err) => addNotification(
                          err?.response?.status === 404 ? "הקובץ לא נמצא בשרת" : "הורדת הקובץ נכשלה", "error"))}
                        className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 text-sm bg-white border border-gray-200 hover:border-brand-400 hover:bg-brand-50 px-3 py-2 rounded-lg transition-colors group"
                        title={att.filename}
                      >
                        {(att.content_type || "").startsWith("image/") ? (
                          <AttachmentThumbnail attachment={att} />
                        ) : (
                          <span>📎</span>
                        )}
                        <span className="font-medium text-gray-700 group-hover:text-brand-700 truncate">{att.filename}</span>
                        <span className="text-gray-400 text-xs whitespace-nowrap">({fmtFileSize(att.size)})</span>
                        <span className="text-gray-400 text-xs whitespace-nowrap">{att.content_type}</span>
                        <span className="text-gray-400 text-xs whitespace-nowrap">{new Date(asUtc(att.created_at)).toLocaleString("he-IL")}</span>
                        <span className="text-gray-400 text-xs whitespace-nowrap">{att.source === "email" ? "מייל" : "ידני"}</span>
                        <span className="text-xs text-brand-600 opacity-0 group-hover:opacity-100 transition-opacity mr-auto">הורד</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "comments" && (
              <div id="ticket-comments-section" className="rounded-lg border bg-gray-50/40 p-3 xl:p-4 space-y-4">
                <h2 className="font-semibold text-gray-700">הערות ({comments.length})</h2>

                {commentsFailed && comments.length === 0 && (
                  <LoadFailed message="לא ניתן לטעון את ההערות" onRetry={refetchComments} />
                )}
                {!commentsFailed && comments.length === 0 && (
                  <p className="text-gray-400 text-sm italic">אין הערות עדיין</p>
                )}

                {sortedComments.map((c) => {
                  const isEmailReply   = c.source === "email_inbound";
                  const isOutboundMail = c.source === "email_outbound";
                  const isAiAgent      = c.source === "ai_agent";
                  const emailUpdateInfo = commentEmailUpdateInfo(c);

                  if (isEmailReply) {
                    return (
                      <div key={c.id} className="rounded-lg border border-orange-200 overflow-hidden text-sm bg-white">
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 border-b border-orange-200">
                          <span className="text-orange-500 text-base leading-none">✉</span>
                          <span className="font-semibold text-orange-800 truncate">
                            תגובת לקוח מ: {c.email_from}
                          </span>
                          <span className="text-xs text-gray-400 mr-auto whitespace-nowrap">
                            {new Date(asUtc(c.created_at)).toLocaleString("he-IL")}
                          </span>
                        </div>
                        <div className="px-4 pt-3">
                          <CommentEmailUpdateLine info={emailUpdateInfo} />
                        </div>
                        {c.body_html ? (
                          <SecureInlineHtml html={c.body_html} attachments={ticketAttachments} className="rte-content text-gray-700 leading-relaxed px-4 pt-3" />
                        ) : (
                          <div className="px-4 py-3 bg-orange-50/30 text-gray-700 whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto">
                            {c.body || <span className="italic text-gray-400">(ללא תוכן)</span>}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (isOutboundMail) {
                    return (
                      <div key={c.id} className="rounded-lg border border-blue-200 overflow-hidden text-sm bg-white">
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border-b border-blue-200">
                          <span className="text-blue-500 text-base leading-none">📤</span>
                          <span className="font-semibold text-blue-800 truncate">
                            מייל נשלח ללקוח על ידי: {c.author_name || "טכנאי"}
                          </span>
                          <span className="text-xs text-gray-400 mr-auto whitespace-nowrap">
                            {new Date(asUtc(c.created_at)).toLocaleString("he-IL")}
                          </span>
                        </div>
                        {c.body_html ? (
                          <SecureInlineHtml html={c.body_html} attachments={ticketAttachments} className="rte-content text-gray-700 leading-relaxed px-4 pt-3 pb-3" />
                        ) : (
                          <div className="px-4 py-3 bg-blue-50/30 text-gray-700 whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto">
                            {c.body || <span className="italic text-gray-400">(ללא תוכן)</span>}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (isAiAgent) {
                    return (
                      <div key={c.id} className="rounded-lg border border-violet-200 overflow-hidden text-sm bg-white">
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-violet-50 border-b border-violet-200">
                          <span className="text-violet-500 text-base leading-none">🤖</span>
                          <span className="font-semibold text-violet-800 truncate">
                            הערה פנימית מ-AI Agent
                          </span>
                          <span className="text-xs text-gray-400 mr-auto whitespace-nowrap">
                            {new Date(asUtc(c.created_at)).toLocaleString("he-IL")}
                          </span>
                        </div>
                        <div className="px-4 py-3 bg-violet-50/30 text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {c.body || <span className="italic text-gray-400">(ללא תוכן)</span>}
                        </div>
                      </div>
                    );
                  }

                  const isEditingThis = editingCommentId === c.id;

                  return (
                    <div
                      key={c.id}
                      className={`rounded-lg p-4 text-sm ${
                        c.is_internal
                          ? "bg-yellow-50 border border-yellow-200"
                          : "bg-white border border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="text-sm font-semibold text-gray-800">
                          נוסף על ידי: {c.author_name || "משתמש לא ידוע"}
                        </div>
                        {!isEditingThis && (canEditComment(c) || canDeleteComment(c)) && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {canEditComment(c) && (
                              <button
                                type="button"
                                onClick={() => startEditComment(c)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                title="ערוך הערה"
                                aria-label="ערוך הערה"
                              >
                                ✏️
                              </button>
                            )}
                            {canDeleteComment(c) && (
                              <button
                                type="button"
                                onClick={() => confirmDeleteComment(c.id)}
                                disabled={deleteComment.isPending}
                                className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-wait"
                                title="מחק הערה"
                                aria-label="מחק הערה"
                              >
                                {deleteComment.isPending ? "..." : "🗑️"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {c.is_internal && (
                          <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded">
                            פנימי
                          </span>
                        )}
                        <span className="text-xs text-gray-400 mr-auto">
                          {new Date(asUtc(c.created_at)).toLocaleString("he-IL")}
                        </span>
                      </div>
                      <CommentEmailUpdateLine info={emailUpdateInfo} />
                      {isEditingThis ? (
                        <div className="space-y-2">
                          <RichTextEditor
                            value={draftCommentBody}
                            onChange={setDraftCommentBody}
                            minRows={3}
                            autoFocus
                            onError={setCommentEditError}
                          />
                          {commentEditError && <p className="text-xs text-red-500">{commentEditError}</p>}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={saveEditComment}
                              disabled={updateComment.isPending}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                            >
                              {updateComment.isPending ? "שומר..." : "שמור"}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditComment}
                              className="px-3 py-1.5 rounded-lg text-xs border border-gray-300 text-gray-600 hover:bg-gray-50"
                            >
                              ביטול
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {c.body_html ? (
                              <SecureInlineHtml
                                html={c.body_html}
                                attachments={ticketAttachments}
                                className="rte-content text-gray-700 leading-relaxed"
                              />
                          ) : (
                            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{c.body}</p>
                          )}
                          {c.edited_at && (
                            <p className="text-xs text-gray-400 mt-1.5 italic">
                              נערך {c.edited_by_name ? `על ידי ${c.edited_by_name} ` : ""}
                              ב-{new Date(asUtc(c.edited_at)).toLocaleString("he-IL")}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (isRichTextEmpty(comment) || isCommentImageProcessing || addComment.isPending) return;
                    if (commentSubmitLockRef.current) return; // one click, one request sequence
                    if (/\bsrc\s*=\s*["']blob:/i.test(comment)) {
                      setCommentBodyError("התמונה עדיין בתהליך העלאה. יש להמתין ולנסות שוב.");
                      return;
                    }
                    commentSubmitLockRef.current = true;
                    addComment.mutate(
                      {
                        body: comment,
                        is_internal: true,
                        additional_technician_ids: additionalTechnicianIds,
                        files: commentFiles,
                      },
                      { onSettled: () => { commentSubmitLockRef.current = false; } }
                    );
                  }}
                  className="space-y-2 pt-2"
                >
                  {/* Quick-reply snippet shortcuts */}
                  {(() => {
                    const SNIPPETS = [
                      { label: "קיבלנו את פנייתך", text: "קיבלנו את פנייתך ואנחנו בודקים את הנושא. נחזור אליך בהקדם." },
                      { label: "אנא הפעל מחדש", text: "אנא נסה להפעיל מחדש את המחשב/השירות ועדכן אותנו האם הבעיה נפתרה." },
                      { label: "צריכים צילום מסך", text: "כדי לסייע לך, אנחנו צריכים מידע נוסף. אנא שלח צילום מסך של השגיאה." },
                      { label: "הנושא מטופל", text: "הנושא מטופל ויפתר בקרוב. תודה על הסבלנות." },
                      { label: "הנושא נפתר", text: "הנושא נפתר מצדנו. אנא עדכן אם הבעיה חוזרת." },
                    ];
                    return (
                      <div className="flex flex-wrap gap-1 pb-0.5">
                        {SNIPPETS.map((s) => (
                          <button
                            key={s.label}
                            type="button"
                            onClick={() => setComment((prev) => {
                              const stripped = (prev || "").replace(/<[^>]*>/g, "").trim();
                              return stripped ? `${prev}\n${s.text}` : s.text;
                            })}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                            title={s.text}
                          >
                            ⚡ {s.label}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  <RichTextEditor
                    id="ticket-comment-input"
                    value={comment}
                    onChange={setComment}
                    placeholder="הוסף הערה... (ניתן להדביק תמונה)"
                    minRows={3}
                    onError={setCommentBodyError}
                    allowImages
                    onProcessingChange={setIsCommentImageProcessing}
                  />
                  {commentBodyError && <p className="text-xs text-red-500">{commentBodyError}</p>}
                  <div className="space-y-1">
                    <label className="block text-xs text-gray-500">שלח עדכון גם לטכנאים</label>
                    <select
                      value=""
                      onChange={(e) => {
                        const techId = e.target.value;
                        if (techId && !additionalTechnicianIds.includes(techId)) {
                          setAdditionalTechnicianIds((prev) => [...prev, techId]);
                        }
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      <option value="">+ הוסף טכנאי לעדכון</option>
                      {technicians
                        .filter((t) => !additionalTechnicianIds.includes(t.id) && t.id !== myTechnicianId)
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.first_name} {t.last_name}
                          </option>
                        ))}
                    </select>
                    {additionalTechnicianIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {additionalTechnicianIds.map((techId) => {
                          const tech = technicians.find((t) => t.id === techId);
                          return (
                            <span
                              key={techId}
                              className="inline-flex items-center gap-1 text-xs bg-brand-50 text-brand-700 border border-brand-200 rounded-full px-2 py-0.5"
                            >
                              {tech ? `${tech.first_name} ${tech.last_name}` : "טכנאי"}
                              <button
                                type="button"
                                onClick={() => setAdditionalTechnicianIds((prev) => prev.filter((id) => id !== techId))}
                                className="text-brand-500 hover:text-brand-700"
                                aria-label="הסר טכנאי"
                              >
                                ✕
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <FileAttachmentPicker
                    inputId="comment-attachments-input"
                    files={commentFiles}
                    onAdd={(files) => setCommentFiles((prev) => [...prev, ...files])}
                    onRemove={(index) => setCommentFiles((prev) => prev.filter((_, i) => i !== index))}
                  />
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isRichTextEmpty(comment) || addComment.isPending || isCommentImageProcessing}
                      className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {addComment.isPending
                        ? "שולח..."
                        : isCommentImageProcessing
                        ? "מעבד תמונה..."
                        : "הוסף הערה"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === "history" && (
              <div className="rounded-lg border bg-gray-50/40 p-3 xl:p-4 space-y-4">
                <h2 className="font-semibold text-gray-700">יומן פעילויות כרטיס</h2>

                {historyFailed && history.length === 0 ? (
                  <LoadFailed message="לא ניתן לטעון את יומן הפעילויות" onRetry={refetchHistory} />
                ) : history.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">אין פעילויות להצגה</p>
                ) : (
                  <div className="space-y-0 rounded-lg border bg-white overflow-hidden">
                    {sortedHistory.map((h) => (
                      <div key={h.id} className="flex items-start gap-3 py-2 px-4 border-b last:border-0 text-sm">
                        <span className="text-base w-5 text-center mt-0.5">{HISTORY_ICONS[h.event] ?? "•"}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded px-1.5 py-0.5 shrink-0">
                              {HISTORY_LABELS[h.event] ?? "פעולה"}
                            </span>
                            <span className="text-gray-700">{h.label}</span>
                          </div>
                          {h.old_value && h.new_value && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {h.old_value} ← {h.new_value}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">{h.actor}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {h.metadata_json?.run_id && (
                            <Link to={`/agent-runs/${h.metadata_json.run_id}`} className="text-xs text-violet-600 hover:underline whitespace-nowrap">
                              הרצה ↗
                            </Link>
                          )}
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {new Date(asUtc(h.created_at)).toLocaleString("he-IL")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "ai-diagnosis" && canAgent("agents_view") && (
              <AgentRunPanel ticketId={ticket.id} ticket={ticket} onFillReply={fillEmailWithDraft} defaultAgentId={assignedAgentId} />
            )}

            {activeTab === "ai-chat" && canAgent("agents_view") && (
              <ConversationPanel ticketId={ticket.id} ticketCompanyId={ticket.company_id} defaultAgentId={assignedAgentId} />
            )}
            {activeTab === "workbench" && canAgent("agents_view") && (
              <TicketEmployeeWorkbench
                ticket={ticket}
                runs={ticketAgentRuns}
                runsFailed={agentRunsFailed}
                runsLoading={agentRunsLoading}
                runsUpdatedAt={agentRunsUpdatedAt}
                onOpenConversation={() => setActiveTab("ai-chat")}
                onOpenDiagnosis={() => setActiveTab("ai-diagnosis")}
                onStartAnalysis={handleStartAiAnalysis}
                isStartingAnalysis={aiAnalyzing}
              />
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-3 xl:p-4 space-y-3 xl:space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-semibold text-gray-700 text-sm">כלים ופעולות</h2>
        </div>

        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
          <div className="border rounded-xl p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-medium text-gray-700 text-sm">✨ עוזר AI</h3>
              <button
                onClick={() => suggestSolution.mutate()}
                disabled={suggestSolution.isPending}
                className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
              >
                {suggestSolution.isPending ? "מעבד..." : "הצע פתרון AI"}
              </button>
            </div>
            {suggestSolution.isError && (
              <p className="text-xs text-red-500">
                {suggestSolution.error?.response?.data?.detail || "שגיאה בקבלת הצעה מ-AI"}
              </p>
            )}
            {showAiPanel && aiSolution && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-violet-800">הצעת פתרון</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{aiSolution}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setComment((prev) => {
                        const stripped = (prev || "").replace(/<[^>]*>/g, "").trim();
                        const html = plainTextToEditorHtml(aiSolution);
                        return stripped ? `${prev}<br><br>${html}` : html;
                      });
                      setShowAiPanel(false);
                      setActiveTab("comments");
                    }}
                    className="text-xs bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg transition-colors"
                  >
                    הכנס כהערה
                  </button>
                  <button
                    onClick={() => setShowAiPanel(false)}
                    className="text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5 rounded-lg border"
                  >
                    סגור
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="border rounded-xl p-3 space-y-3">
            <h3 className="font-medium text-gray-700 text-sm">🔍 חיפוש SharePoint</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={spFolderId}
                onChange={(e) => setSpFolderId(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm text-gray-700 bg-white sm:w-44 shrink-0"
              >
                <option value="">כל התיקיות</option>
                {spFolders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <input
                type="text"
                value={spQuery}
                onChange={(e) => setSpQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    spSearch.mutate({ query: spQuery.trim() || ticket.title, folderId: spFolderId });
                }}
                placeholder={ticket.title}
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
                dir="auto"
              />
              <button
                onClick={() => spSearch.mutate({ query: spQuery.trim() || ticket.title, folderId: spFolderId })}
                disabled={spSearch.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shrink-0"
              >
                {spSearch.isPending ? "מחפש..." : "חפש"}
              </button>
            </div>
            {spSearch.isError && (
              <p className="text-xs text-red-500">
                {spSearch.error?.response?.data?.detail || "חיפוש נכשל — בדוק הרשאות Sites.Read.All"}
              </p>
            )}
            {showSpPanel && spSearch.data && (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {spSearch.data.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">לא נמצאו תוצאות עבור "{spQuery.trim() || ticket.title}"</p>
                ) : (
                  spSearch.data.map((item, idx) => (
                    <div key={idx} className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-blue-700 hover:underline truncate block leading-snug"
                      >
                        {item.type === "folder" ? "📁" : "📄"} {item.name}
                      </a>
                      {item.folder_path && (
                        <span style={{ fontSize: 11 }} className="text-gray-400 truncate block leading-snug mt-0.5" dir="ltr">
                          📁 {item.folder_path}
                        </span>
                      )}
                    </div>
                  ))
                )}
                <button
                  onClick={() => setShowSpPanel(false)}
                  className="text-xs text-gray-400 hover:text-gray-600 underline"
                >
                  סגור תוצאות
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

    </div>

    {/* ── Move Queue Dialog ─────────────────────────────────────────────────── */}
    {showMoveQueue && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" dir="rtl">
        <div role="dialog" aria-modal="true" aria-labelledby="move-queue-title" className="bg-white rounded-xl shadow-xl border p-6 w-[28rem] space-y-4">
          <div className="flex items-center justify-between">
            <h3 id="move-queue-title" className="text-base font-semibold text-gray-800">העבר קריאה לתור אחר</h3>
            <button onClick={() => { moveQueueMutation.reset(); setShowMoveQueue(false); }} aria-label="סגור" className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
          </div>

          <div className="flex gap-2">
            {[
              { id: "ticket",      label: "📋 כרטיסים", cls: "border-blue-400 bg-blue-50 text-blue-700" },
              { id: "project",     label: "📁 פרויקטים", cls: "border-purple-400 bg-purple-50 text-purple-700" },
              { id: "maintenance", label: "🔧 תחזוקה",   cls: "border-green-400 bg-green-50 text-green-700" },
            ].map((q) => (
              <button
                key={q.id}
                onClick={() => setMoveQueueTarget(q.id)}
                className={`flex-1 py-2 px-3 rounded-lg border-2 text-xs font-semibold transition-all ${
                  moveQueueTarget === q.id ? q.cls : "border-gray-200 text-gray-500 hover:border-gray-300"
                }`}
              >
                {q.label}
              </button>
            ))}
          </div>

          {moveQueueTarget === "project" && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">שייך לפרויקט קיים (אופציונלי)</label>
              <select
                value={moveProjectId}
                onChange={(e) => setMoveProjectId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">— ייווצר פרויקט חדש אוטומטית —</option>
                {projectsList.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}{p.company?.name ? ` (${p.company.name})` : ""}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {moveProjectId
                  ? "הקריאה תיווסף כמשימה תחת הפרויקט הנבחר"
                  : "ייווצר פרויקט חדש בשם הקריאה, והקריאה תשוייך אליו כמשימה"}
              </p>
            </div>
          )}

          {moveQueueTarget === "maintenance" && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">קטגוריית תחזוקה</label>
              <select
                value={moveCategory}
                onChange={(e) => setMoveCategory(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="backups">גיבויים</option>
                <option value="m365">M365</option>
                <option value="av_edr">AV/EDR</option>
                <option value="updates">עדכונים ותיקונים</option>
                <option value="firewall">חומת אש</option>
                <option value="email_security">אבטחת מייל</option>
                <option value="reports">דוחות</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                ייווצר פריט תחזוקה חדש בשם הקריאה בקטגוריה שנבחרה
              </p>
            </div>
          )}

          {moveQueueMutation.isError && (
            <p className="text-xs text-red-500">
              {moveQueueMutation.error?.response?.data?.detail || "שגיאה בהעברה"}
            </p>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => { moveQueueMutation.reset(); setShowMoveQueue(false); }} className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
            <button
              onClick={() => moveQueueMutation.mutate({
                queue_type: moveQueueTarget,
                project_id: moveQueueTarget === "project" && moveProjectId ? moveProjectId : null,
                maintenance_category: moveQueueTarget === "maintenance" ? moveCategory : null,
              })}
              disabled={moveQueueMutation.isPending}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-50 ${
                moveQueueTarget === "project" ? "bg-purple-600 hover:bg-purple-700" :
                moveQueueTarget === "maintenance" ? "bg-green-600 hover:bg-green-700" :
                "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {moveQueueMutation.isPending ? "מעביר..." : "העבר"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Split email to new ticket Modal ──────────────────────────────────── */}
    {splitEmailTarget && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" dir="rtl">
        <div role="dialog" aria-modal="true" aria-labelledby="split-email-title" className="bg-white rounded-xl shadow-xl border p-6 w-[28rem] max-w-[90vw] space-y-4">
          <div className="flex items-center justify-between">
            <h3 id="split-email-title" className="text-base font-semibold text-gray-800">פיצול מייל לקריאה חדשה</h3>
            <button onClick={closeSplitEmailModal} aria-label="סגור" className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
          </div>

          {splitEmailResult ? (
            <div className="space-y-4">
              <p className="text-sm text-emerald-700">
                קריאה #{splitEmailResult.ticket_number} נוצרה בהצלחה מהמייל
              </p>
              <div className="flex gap-2 justify-end">
                <button onClick={closeSplitEmailModal} className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">סגור</button>
                <Link
                  to={`/tickets/${splitEmailResult.id}`}
                  onClick={closeSplitEmailModal}
                  className="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
                >
                  פתח קריאה חדשה
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3 space-y-1">
                <div>שולח: <span className="text-gray-700 font-medium">{splitEmailTarget.from_name || splitEmailTarget.from_email}</span></div>
                <div>תאריך: <span className="text-gray-700 font-medium">{new Date(asUtc(splitEmailTarget.received_at)).toLocaleString("he-IL")}</span></div>
                <div>נושא: <span className="text-gray-700 font-medium">{splitEmailTarget.subject || "(ללא נושא)"}</span></div>
                <div className="line-clamp-2">
                  תצוגה מקדימה: <span className="text-gray-700">{compactText(splitEmailTarget.body_text || "") || "(ללא תוכן)"}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">כותרת הקריאה החדשה</label>
                <input
                  type="text"
                  aria-label="כותרת הקריאה החדשה"
                  value={splitEmailTitle}
                  onChange={(e) => setSplitEmailTitle(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">טכנאי</label>
                <select
                  aria-label="טכנאי"
                  value={splitEmailTechnicianId}
                  onChange={(e) => setSplitEmailTechnicianId(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">ללא שיבוץ</option>
                  {technicians.map((tech) => (
                    <option key={tech.id} value={tech.id}>{tech.first_name} {tech.last_name}</option>
                  ))}
                </select>
              </div>

              {splitEmailError && <p className="text-xs text-red-500">{splitEmailError}</p>}

              <div className="flex gap-2 justify-end pt-1">
                <button onClick={closeSplitEmailModal} className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
                <button
                  onClick={confirmSplitEmail}
                  disabled={splitFromEmail.isPending}
                  className="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
                >
                  {splitFromEmail.isPending ? "יוצר..." : "צור קריאה חדשה"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}

    {/* ── Close Ticket — Resolution Note Modal ─────────────────────────────── */}
    {showCloseModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" dir="rtl">
        <div role="dialog" aria-modal="true" aria-labelledby="close-ticket-modal-title" className="bg-white rounded-xl shadow-xl border p-6 w-[30rem] max-w-[90vw] space-y-4">
          <div className="flex items-center justify-between">
            <h3 id="close-ticket-modal-title" className="text-base font-semibold text-gray-800">סגירת קריאה</h3>
            <button onClick={() => { updateTicket.reset(); setShowCloseModal(false); }} aria-label="סגור" className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
          </div>
          <p className="text-sm text-gray-600">
            מה פתר את הבעיה? (אופציונלי — עוזר לשיפור הידע העתידי של המערכת)
          </p>
          {aiResolutionSuggestion && !closeResolutionNote.trim() && canAgent("agents_view") && (
            <div className="rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-xs space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-violet-700 font-medium">הצעת AI מניתוח קודם:</span>
                <button
                  type="button"
                  onClick={() => {
                    const parts = [aiResolutionSuggestion.likely_issue];
                    if (aiResolutionSuggestion.recommended_next_step) parts.push(aiResolutionSuggestion.recommended_next_step);
                    setCloseResolutionNote(parts.join(". "));
                  }}
                  className="text-[10px] bg-violet-100 hover:bg-violet-200 text-violet-700 border border-violet-300 px-2 py-0.5 rounded transition-colors shrink-0"
                >
                  מלא מ-AI
                </button>
              </div>
              {aiResolutionDegraded && (
                <span
                  data-testid="ticket-close-ai-degraded"
                  className="inline-block text-[10px] font-medium leading-none bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5"
                >
                  {aiResolutionDegraded}
                </span>
              )}
              <p className="text-violet-800 leading-relaxed line-clamp-2">{aiResolutionSuggestion.likely_issue}</p>
            </div>
          )}
          <textarea
            autoFocus
            value={closeResolutionNote}
            onChange={(e) => setCloseResolutionNote(e.target.value)}
            placeholder="לדוגמה: הותקן עדכון KB5034441, הוגדר מחדש DNS, הסיסמה אופסה..."
            rows={4}
            maxLength={4000}
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          {closeResolutionNote.trim().length >= 5 && (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={saveAsKnowledge}
                onChange={(e) => setSaveAsKnowledge(e.target.checked)}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              שמור פתרון כמאמר ידע לאישור מנהל
            </label>
          )}
          {updateTicket.isError && (
            // the dialog stays open when the close fails, so the reason is shown here (a toast can be off screen)
            <p role="alert" className="text-xs text-red-600">
              {detailText(updateTicket.error?.response?.data?.detail, "שגיאה בסגירת הקריאה")}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => { updateTicket.reset(); setShowCloseModal(false); setSaveAsKnowledge(false); }}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:border-gray-400"
            >
              ביטול
            </button>
            <button
              onClick={() => {
                const note = closeResolutionNote.trim();
                if (saveAsKnowledge && note.length >= 5) {
                  pendingKbData.current = { title: ticket.title, problem_pattern: aiResolutionSuggestion?.likely_issue || ticket.title, solution: note };
                } else {
                  pendingKbData.current = null;
                }
                // The dialog and the typed note are cleared only when the close SUCCEEDED: clearing them first lost a long
                // note when the request failed.
                updateTicket.mutate(
                  { status: "closed", ...(note ? { resolution_note: note } : {}) },
                  { onSuccess: () => { setShowCloseModal(false); setSaveAsKnowledge(false); setCloseResolutionNote(""); } },
                );
              }}
              disabled={updateTicket.isPending}
              className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-800 text-white font-medium disabled:opacity-50"
            >
              {updateTicket.isPending ? "סוגר..." : "סגור קריאה"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Contact picker (add contact email to To) ──────────────────────────── */}
    {showContactPicker && (
      <AddContactModal
        title="בחירת איש קשר"
        search={contactSearch}
        onSearchChange={setContactSearch}
        searchPlaceholder="חיפוש לפי שם, אימייל, חברה או טלפון..."
        onClose={() => setShowContactPicker(false)}
      >
        {contactsLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
          </div>
        ) : filteredContacts.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-6">לא נמצאו אנשי קשר</p>
        ) : (
          filteredContacts.map((contact) => (
            <button
              key={contact.id}
              type="button"
              disabled={!contact.email}
              onClick={() => addContactEmailToRecipient(contact)}
              className="w-full text-right px-3 py-2 rounded-lg border hover:border-brand-400 hover:bg-brand-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="text-sm font-medium text-gray-800 truncate">
                {contact.first_name} {contact.last_name || ""}
                {contact.company_name && (
                  <span className="text-xs text-gray-400 font-normal"> · {contact.company_name}</span>
                )}
              </div>
              <div className="text-xs text-gray-500 truncate" dir="ltr">
                {contact.email || "ללא כתובת אימייל"}
              </div>
            </button>
          ))
        )}
      </AddContactModal>
    )}

    {/* ── Add sender as new contact ─────────────────────────────────────────── */}
    {showAddSenderContact && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" dir="rtl">
        <div role="dialog" aria-modal="true" aria-labelledby="add-sender-contact-title" className="bg-white rounded-xl shadow-xl border p-6 w-[26rem] max-w-[90vw] space-y-4">
          <div className="flex items-center justify-between">
            <h3 id="add-sender-contact-title" className="text-base font-semibold text-gray-800">
              {contactCreationContext === "email-thread" ? "הוספת שולח המייל לאנשי הקשר" : "להוסיף את השולח כאיש קשר חדש?"}
            </h3>
            <button
              onClick={closeAddSenderContact}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              aria-label="סגור"
            >
              ✕
            </button>
          </div>

          {duplicateContact ? (
            <div className="space-y-3">
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {addContactError || "איש קשר עם אימייל זה כבר קיים"}
              </p>
              <div className="border rounded-lg px-3 py-2 text-sm">
                <p className="font-medium text-gray-700">{duplicateContact.first_name} {duplicateContact.last_name || ""}</p>
                <p className="text-xs text-gray-400" dir="ltr">{duplicateContact.email}</p>
                {duplicateContact.company_name && <p className="text-xs text-gray-400">{duplicateContact.company_name}</p>}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeAddSenderContact}
                  className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
                >
                  ביטול
                </button>
                <button
                  type="button"
                  onClick={applyDuplicateContact}
                  className="px-4 py-1.5 rounded-lg text-sm bg-brand-600 text-white hover:bg-brand-700"
                >
                  בחר איש קשר זה ועדכן שולח
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400">שם פרטי</label>
                <input
                  type="text"
                  value={addContactDraft.first_name}
                  onChange={(e) => setAddContactDraft((d) => ({ ...d, first_name: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400">שם משפחה</label>
                <input
                  type="text"
                  value={addContactDraft.last_name}
                  onChange={(e) => setAddContactDraft((d) => ({ ...d, last_name: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400">אימייל</label>
                <input
                  type="email"
                  dir="ltr"
                  value={addContactDraft.email}
                  onChange={(e) => setAddContactDraft((d) => ({ ...d, email: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <SenderContactPhoneField
                value={addContactDraft.phone}
                onChange={(e) => setAddContactDraft((d) => ({ ...d, phone: e.target.value }))}
              />
              <div>
                <label className="text-xs text-gray-400">חברה (אופציונלי)</label>
                <select
                  value={addContactDraft.company_id}
                  onChange={(e) => setAddContactDraft((d) => ({ ...d, company_id: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">ללא חברה</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {addContactError && <p className="text-xs text-red-500">{addContactError}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeAddSenderContact}
                  className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
                >
                  ביטול
                </button>
                <button
                  type="button"
                  onClick={confirmAddSenderContact}
                  disabled={createSenderContact.isPending}
                  className="px-4 py-1.5 rounded-lg text-sm bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {createSenderContact.isPending ? "מוסיף..." : "אישור והוספה"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}

// Keyed on the route id so navigating between tickets (Previous/Next, or any other
// in-app link) fully remounts the view — clearing drafts, edit-mode toggles and the
// active tab from the previous ticket instead of leaking them into the new one.
export default function TicketDetail() {
  const { id } = useParams();
  return <TicketDetailInner key={id} />;
}
